<template>
  <!-- Input Area -->
  <div class="chat-input-shell">
  <div
    class="input-area"
    :class="{ 'dragging': isDragging }"
    @dragover.prevent="handleDragOver"
    @dragleave.prevent="handleDragLeave"
    @drop.prevent="handleDrop"
  >
    <!-- Expanded Role List Popover (Removed in favor of CharacterManager) -->

    <!-- Scroll to bottom notification -->
    <transition name="fade">
      <div v-if="showScrollTip" class="scroll-tip" @click="$emit('scroll-to-bottom', true)">
        <div class="scroll-tip-content">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 13l5 5 5-5M7 6l5 5 5-5"/></svg>
          <span>{{ $tl('有新内容，点击回到底部') }}</span>
        </div>
      </div>
    </transition>

    <!-- New Chat Tip -->
    <transition name="fade">
      <div v-if="messagesLength > 6 && isInputFocused" class="conversation-tip">
        <span class="tip-icon">💡</span>
        <span class="tip-text">
          {{ $tl('小贴士：如果新的问题与之前不相关，建议') }}
          <span class="tip-link" @click="$emit('create-new-chat')">{{ $tl('新建会话') }}</span>
          {{ $tl('，这样 AI 的理解会更准确哦。') }}
        </span>
      </div>
    </transition>

    <!-- Cloud Update Tip -->
    <transition name="fade">
      <div v-if="cloudUpdatePending && !(isMobile && isCollapsed)" class="cloud-update-banner">
        <div class="banner-content">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" class="banner-icon">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
          <span class="banner-text">{{ $tl('云端数据与本地不同') }}</span>
        </div>
        <div class="banner-actions">
          <button class="banner-btn primary" @click="handleCloudAction('restore')">{{ $tl('下载云端') }}</button>
          <button class="banner-btn secondary" @click="handleCloudAction('overwrite')">{{ $tl('覆盖云端') }}</button>
        </div>
      </div>
    </transition>

    <div
      ref="inputWrapper"
      class="input-wrapper"
      :class="{ 'voice-mode-active': isVoiceUiActive, 'is-collapsed': isMobile && isCollapsed, 'is-morphing': isMorphing }"
      @click="handleWrapperClick"
    >
      <!-- Collapsed overlay: minimal search content, shown when collapsed -->
      <div class="collapsed-overlay" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
        <span class="collapsed-overlay-text">{{ inputText.trim() ? inputText : $tl('有什么可以帮你') }}</span>
      </div>

      <div class="input-inner">
      <div v-if="(attachments.length > 0 || isUploading)" class="attachment-preview">
          <div v-if="isUploading" class="uploading-spinner">
            <svg class="spinner" viewBox="0 0 50 50"><circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle></svg>
            <span>Uploading...</span>
          </div>
          
          <div class="attachments-list" v-if="attachments.length > 0">
              <div v-for="(att, index) in attachments" :key="index" class="preview-item">
                <!-- Image Preview -->
                <div v-if="att.type === 'image'" class="preview-img-container" @click="$emit('preview-image', att.url)" :data-url="att.url">
                    <img :src="att.url" :alt="att.name">
                </div>
                <!-- File Preview -->
                <div v-else class="preview-file-container" @click="openFile(att.url)" :title="att.url ? $tl('点击打开文件') : ''" :class="{ 'clickable': att.url }" :data-url="att.url">
                    <div class="file-icon">
                      <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                    </div>
                    <span class="file-name">{{ att.name }}</span>
                </div>
                <!-- Global Remove Button -->
                <button class="remove-btn" @click="$emit('remove-attachment', index)" :title="$tl('Remove attachment')">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                </button>
              </div>
          </div>
      </div>
      <div class="mode-tabs">

          <!-- Thinking Toggle -->
          <button 
            class="tool-tab mode-thinking"
            :class="{ active: thinkingEnabled }"
            @click="handleThinkingToggle"
            title="Deep Thinking Mode"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
              <path d="M9.5 11.5a3.5 3.5 0 0 1 0-7 3.5 3.5 0 0 1 6.1-2.3A4.5 4.5 0 0 1 20 6.5a4.5 4.5 0 0 1-1 8.9 3.6 3.6 0 0 1-6.5 2.1 3.6 3.6 0 0 1-6.5-2.1 4.5 4.5 0 0 1-1-8.9 3.5 3.5 0 0 1 4.5 5Z"></path>
              <path d="M9 8.5c1.2 1 1.2 2.3 0 3.5m6-3.5c-1.2 1-1.2 2.3 0 3.5M8.5 15c1.1-.8 2.1-.8 3 0m4-0c-1.1-.8-2.1-.8-3 0"></path>
            </svg>
            <span>{{ $tl('深度思考') }}</span>
          </button>

        
          <!-- Web Search Toggle -->
          <button 
            class="tool-tab mode-web-search"
            :class="{ active: webSearchEnabled }"
            @click="handleWebSearchToggle"
            title="Web Search Mode"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="9"></circle><path d="M3 12h18M12 3a13.5 13.5 0 0 1 0 18M12 3a13.5 13.5 0 0 0 0 18"></path>
            </svg>
            <span>{{ $tl('联网搜索') }}</span>
          </button>
          
          <!-- Plan Mode Toggle -->
          <button 
            class="tool-tab mode-plan"
            :class="{ active: planEnabled }"
            @click="handlePlanToggle"
            title="Plan Mode"
          >
           <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
              <rect x="3" y="4" width="18" height="17" rx="2"></rect><path d="M16 2v4M8 2v4M3 10h18M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01"></path>
            </svg>
            <span>{{ $tl('Plan模式') }}</span>
          </button>

          <!-- Local Workspace -->
          <button
            class="tool-tab workspace-mode-tab mode-workspace"
            :class="{ active: workspaceFiles.length > 0 }"
            type="button"
            title="打开本地工作区"
            @click="$emit('open-workspace')"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M3 7h6l2 2h10v10H3z"></path>
              <path d="M3 7V5h7l2 2"></path>
            </svg>
            <span>{{ $tl('工作区') }}</span>
            <span v-if="workspaceFiles.length" class="workspace-count">{{ workspaceFiles.length }}</span>
          </button>

          <!-- Knowledge Base Association Toggle -->
          <button
            class="tool-tab mode-knowledge"
            :class="{ active: knowledgeBaseAssociationEnabled }"
            @click="handleKnowledgeBaseAssociationToggle"
            title="Knowledge Base Association Mode"
          >
           <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 256 256"><path d="M231.65,194.55,198.46,36.75a16,16,0,0,0-19-12.39L132.65,34.42a16.08,16.08,0,0,0-12.3,19l33.19,157.8A16,16,0,0,0,169.16,224a16.25,16.25,0,0,0,3.38-.36l46.81-10.06A16.09,16.09,0,0,0,231.65,194.55ZM136,50.15c0-.06,0-.09,0-.09l46.8-10,3.33,15.87L139.33,66Zm6.62,31.47,46.82-10.05,3.34,15.9L146,97.53Zm6.64,31.57,46.82-10.06,13.3,63.24-46.82,10.06ZM216,197.94l-46.8,10-3.33-15.87L212.67,182,216,197.85C216,197.91,216,197.94,216,197.94ZM104,32H56A16,16,0,0,0,40,48V208a16,16,0,0,0,16,16h48a16,16,0,0,0,16-16V48A16,16,0,0,0,104,32ZM56,48h48V64H56Zm0,32h48v96H56Zm48,128H56V192h48v16Z"></path></svg>
            <span>{{ $tl('检索知识库') }}</span>
          </button>

            <!-- Image Generation Toggle -->
          <button 
            class="tool-tab mode-image-gen"
            :class="{ active: imageGenEnabled }"
            @click="handleImageGenToggle"
            title="Image Generation Mode"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
              <path d="M12 3a9 9 0 1 0 9 9c0-.6-.4-1-1-1h-2a2 2 0 0 1-2-2V7a2 2 0 0 0-2-2h-2.5A1.5 1.5 0 0 1 12 3Z"></path><circle cx="7.5" cy="12" r="1"></circle><circle cx="10" cy="7.5" r="1"></circle><circle cx="15" cy="7.5" r="1"></circle><circle cx="17" cy="13" r="1"></circle>
            </svg>
            <span>{{ $tl('AI绘画') }}</span>
          </button>
          
          <!-- Image Config Selectors (Only show when Image Generation is active) -->
          <div v-show="imageGenEnabled" class="image-config-selectors">
            <select class="image-config-select" :value="imageAspectRatio" @change="updateImageConfig('aspectRatio', $event.target.value)" :title="$tl('宽高比')">
              <option value="1:1">1:1</option>
              <option value="2:3">2:3</option>
              <option value="3:2">3:2</option>
              <option value="3:4">3:4</option>
              <option value="4:3">4:3</option>
              <option value="4:5">4:5</option>
              <option value="5:4">5:4</option>
              <option value="9:16">9:16</option>
              <option value="16:9">16:9</option>
              <option value="21:9">21:9</option>
              <option value="1:4">1:4 (Gemini 3.1)</option>
              <option value="1:8">1:8 (Gemini 3.1)</option>
              <option value="4:1">4:1 (Gemini 3.1)</option>
              <option value="8:1">8:1 (Gemini 3.1)</option>
            </select>
            <select class="image-config-select" :value="imageSize" @change="updateImageConfig('size', $event.target.value)" :title="$tl('分辨率')">
              <option value="0.5K">0.5K (Gemini 3.1)</option>
              <option value="1K">1K</option>
              <option value="2K">2K</option>
              <option value="4K">4K</option>
            </select>
          </div>

      </div>

      <!-- Files currently available in this conversation's workspace -->
      <div v-if="workspaceFiles.length" class="workspace-file-strip" aria-label="工作区文件">
        <button
          v-for="file in workspaceFiles"
          :key="file.id"
          type="button"
          class="workspace-file-chip"
          :title="file.name"
          @click="$emit('open-workspace', file)"
        >
          <span class="workspace-file-chip-mark">{{ workspaceFileType(file) }}</span>
          <span class="workspace-file-chip-name">{{ file.name }}</span>
        </button>
      </div>

      <transition name="mention-fade">
        <div v-if="mentionOpen" class="companion-mention-popover" role="listbox">
          <button
            v-for="(companion, index) in filteredCompanions"
            :key="companion.id"
            type="button"
            class="companion-mention-option"
            :class="{ active: index === mentionIndex }"
            @mousedown.prevent="selectCompanionMention(companion)"
          >
            <span class="mention-option-name" v-html="highlightCompanionName(companion.name)"></span>
          </button>
          <div v-if="filteredCompanions.length === 0" class="mention-empty">没有找到匹配的伙伴</div>
        </div>
      </transition>

      <div
        ref="textarea"
        class="companion-editor"
        contenteditable="true"
        role="textbox"
        aria-multiline="true"
        :data-placeholder="$tl('有什么问题尽管问我...')"
        @focus="$emit('focus')"
        @blur="$emit('blur')"
        @input="handleEditorInput"
        @keydown="handleEditorKeydown"
        @click="handleEditorClick"
        @paste="handlePaste"
      ></div>

      <!-- Common Instructions (Quick Prompts) - shown when URL brought in attachments -->
      <transition name="fade">
        <div
          v-if="hasUrlAttachments && commonInstructions.length > 0 && !inputText.trim()"
          class="common-instructions"
        >
          <span class="ci-label">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z"></path>
            </svg>
            <span>{{ $tl('试试这样问') }}</span>
          </span>
          <div class="ci-chips">
            <button
              v-for="(instruction, index) in commonInstructions"
              :key="index"
              class="instruction-chip"
              :title="instruction"
              @click="applyInstruction(instruction)"
            >
              <span class="chip-text">{{ instruction }}</span>
              <svg class="chip-arrow" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </button>
          </div>
        </div>
      </transition>
      
      <div class="toolbar">
        <div class="left-tools" :class="{ 'has-attachment-menu': showAttachmentMenu }">
          <!-- Companion selector -->
          <div class="tool-group">
            <button
              v-if="activeCompanions.length === 0"
              class="tool-tab role-selector" 
              @click="$emit('toggle-character-manager')"
              :title="$tl('选择伙伴')"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256">
                <path d="M256,136a8,8,0,0,1-8,8H232v16a8,8,0,0,1-16,0V144H200a8,8,0,0,1,0-16h16V112a8,8,0,0,1,16,0v16h16A8,8,0,0,1,256,136Zm-57.87,58.85a8,8,0,0,1-12.26,10.3C165.75,181.19,138.09,168,108,168s-57.75,13.19-77.87,37.15a8,8,0,0,1-12.25-10.3c14.94-17.78,33.52-30.41,54.17-37.17a68,68,0,1,1,71.9,0C164.6,164.44,183.18,177.07,198.13,194.85ZM108,152a52,52,0,1,0-52-52A52.06,52.06,0,0,0,108,152Z"></path>
              </svg>
              <span>{{ $tl('伙伴选择') }}</span>
            </button>
            <div
              v-else
              class="active-role-chip active-companion-summary"
              :title="$tl('点击更换伙伴')"
              @click="$emit('toggle-character-manager')"
            >
              <span class="role-icon">{{ activeCompanions[0].icon || '🤝' }}</span>
              <span class="role-name">{{ activeCompanions[0].name }}</span>
              <span v-if="activeCompanions.length > 1" class="companion-more-count">+{{ activeCompanions.length - 1 }}</span>
              <button class="remove-role-btn" @click.stop="$emit('remove-companion', activeCompanions[0].id)" :title="$tl('移除伙伴')">×</button>
            </div>
          </div>

          <!-- Voice Input -->
          <div class="voice-inline-wrap">
            <button
              type="button"
              class="icon-btn voice-btn"
              :class="{ active: isVoiceUiActive }"
              :disabled="voiceState === 'recognizing'"
              :title="voiceButtonTitle"
              :aria-label="voiceButtonTitle"
              :aria-pressed="isVoiceUiActive ? 'true' : 'false'"
              @pointerdown="handleVoicePointerDown"
              @keydown="handleVoiceKeyDown"
              @contextmenu.prevent
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256">
                <path d="M128,176a48.05,48.05,0,0,0,48-48V64a48,48,0,0,0-96,0v64A48.05,48.05,0,0,0,128,176ZM96,64a32,32,0,0,1,64,0v64a32,32,0,0,1-64,0Zm40,143.6V240a8,8,0,0,1-16,0V207.6A80.11,80.11,0,0,1,48,128a8,8,0,0,1,16,0,64,64,0,0,0,128,0,8,8,0,0,1,16,0A80.11,80.11,0,0,1,136,207.6Z"></path>
              </svg>
            </button>
          </div>
          
          <!-- Attachment source menu -->
          <div
            ref="attachmentMenuWrap"
            class="attachment-menu-wrap"
            @pointerenter="handleAttachmentWrapPointerEnter"
            @pointerleave="handleAttachmentWrapPointerLeave"
            @focusin="openAttachmentMenu"
            @focusout="scheduleAttachmentMenuClose"
          >
            <button
              ref="attachmentButton"
              class="icon-btn attachment-btn"
              :title="$tl('Attach File')"
              aria-haspopup="menu"
              :aria-expanded="showAttachmentMenu ? 'true' : 'false'"
              @click.stop="handleAttachmentButtonClick"
              @keydown.esc.stop="closeAttachmentMenu"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256"><path d="M209.66,122.34a8,8,0,0,1,0,11.32l-82.05,82a56,56,0,0,1-79.2-79.21L147.67,35.73a40,40,0,1,1,56.61,56.55L105,193A24,24,0,1,1,71,159L154.3,74.38A8,8,0,1,1,165.7,85.6L82.39,170.31a8,8,0,1,0,11.27,11.36L192.93,81A24,24,0,1,0,159,47L59.76,147.68a40,40,0,1,0,56.53,56.62l82.06-82A8,8,0,0,1,209.66,122.34Z"></path></svg>
            </button>
            <div
              v-if="showAttachmentMenu"
              class="attachment-menu"
              :class="{ 'is-below': attachmentMenuPlacement === 'below' }"
              role="menu"
              @pointerenter="handleAttachmentMenuPointerEnter"
              @pointerleave="handleAttachmentMenuPointerLeave"
            >
              <div class="attachment-menu-heading">添加文件</div>
              <button type="button" class="attachment-menu-option" role="menuitem" @click.stop="selectAttachmentSource('workspace')">
                <span class="attachment-menu-icon workspace-menu-icon">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7h6l2 2h10v10H3z"></path><path d="M3 7V5h7l2 2"></path></svg>
                </span>
                <span class="attachment-menu-copy">
                  <strong>工作区文件</strong>
                  <small>{{ workspaceFiles.length ? `已有 ${workspaceFiles.length} 个文件，可继续添加` : '选择本地文件放入工作区' }}</small>
                </span>
              </button>
              <button type="button" class="attachment-menu-option" role="menuitem" @click.stop="selectAttachmentSource('context')">
                <span class="attachment-menu-icon context-menu-icon">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21.44 11.05 12.25 20.24a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.19 9.19a2 2 0 1 1-2.83-2.83l8.49-8.49"></path></svg>
                </span>
                <span class="attachment-menu-copy">
                  <strong>上下文附件</strong>
                  <small>上传文件并随本次消息发送</small>
                </span>
              </button>
            </div>
          </div>
          <input type="file" ref="fileInput"  @change="handleFileUpload" style="display: none" accept="image/*, .doc, .docx, .ppt, .pptx, .pdf, .xls, .xlsx, .txt, .csv, .json" multiple>
          <input type="file" ref="workspaceFileInput" @change="handleWorkspaceFilePick" style="display: none" multiple>
        </div>

        <button class="send-btn" @click="isSending ? $emit('stop-generation') : $emit('send-message')" :disabled=" (!isSending && (!inputText.trim() && attachments.length === 0))" :title="isSending ? $tl('Stop generation') : $tl('Send message')">
          <svg v-if="!isSending" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="22" y1="2" x2="11" y2="13"></line>
            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
          </svg>
          <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="white"><rect x="6" y="6" width="12" height="12" rx="2" ry="2" /></svg>
        </button>
      </div>
    </div>
  </div>
  </div>
  <!-- Dropping anywhere on the overlay outside a target defaults to attachment -->
  <div
    class="drag-overlay"
    v-if="isDragging"
    @dragover.prevent
    @drop.prevent.stop="handleAttachmentDrop"
  >
    <div class="drag-content">
      <div class="drag-heading">选择文件放置位置</div>
      <div class="drag-subheading">同一批文件可选择不同用途，处理方式不会互相影响</div>
      <div class="drag-targets">
        <div
          class="drag-target drag-target-attachment"
          :class="{ 'is-active': activeDropTarget === 'attachment', 'is-default': !activeDropTarget }"
          @dragover.prevent.stop="handleTargetDragOver($event, 'attachment')"
          @dragleave.prevent.stop="handleTargetDragLeave($event, 'attachment')"
          @drop.prevent.stop="handleAttachmentDrop"
        >
          <span class="drag-target-badge">默认</span>
          <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
          <strong>作为附件</strong>
          <small>全文读取并发送给模型</small>
        </div>
        <div
          class="drag-target drag-target-workspace"
          :class="{ 'is-active': activeDropTarget === 'workspace' }"
          @dragover.prevent.stop="handleTargetDragOver($event, 'workspace')"
          @dragleave.prevent.stop="handleTargetDragLeave($event, 'workspace')"
          @drop.prevent.stop="handleWorkspaceDrop"
        >
          <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M3 7.5A2.5 2.5 0 0 1 5.5 5h4l2 2h7A2.5 2.5 0 0 1 21 9.5v8A2.5 2.5 0 0 1 18.5 20h-13A2.5 2.5 0 0 1 3 17.5z"></path><path d="M3 10h18"></path></svg>
          <strong>放入工作区</strong>
          <small>本地保存，模型按需读取</small>
        </div>
      </div>
      <div class="drag-footnote">在其他位置松开默认作为附件，拖出窗口可取消</div>
    </div>
  </div>
</div>
</template>

<script>
import { speechService, HoldToTalk, mergeSpeechText } from '@/utils/speech';
import { compressMarkdownTable } from '@/utils/text';
import { MARKITDOWN_API_URL } from '@/api/config';
import { summarizeAttachmentAsync } from '@/api/chat';
import { store, mutations } from '@/store';
import { showToast } from '@/utils/toast';
import { pinyin } from 'pinyin-pro';

export default {
  name: 'ChatInputBox',
  props: {
    inputText: {
      type: String,
      default: ''
    },
    isDragging: {
      type: Boolean,
      default: false
    },
    showScrollTip: {
      type: Boolean,
      default: false
    },
    messagesLength: {
      type: Number,
      default: 0
    },
    isInputFocused: {
      type: Boolean,
      default: false
    },
    attachments: {
      type: Array,
      default: () => []
    },
    isUploading: {
      type: Boolean,
      default: false
    },
    currentRole: {
      type: Object,
      required: true
    },
    activeCompanions: {
      type: Array,
      default: () => []
    },
    availableCompanions: {
      type: Array,
      default: () => []
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    isCollapsed: {
      type: Boolean,
      default: false
    },
    isModeExpanded: {
      type: Boolean,
      default: false
    },
    thinkingEnabled: {
      type: Boolean,
      default: false
    },
    webSearchEnabled: {
      type: Boolean,
      default: false
    },
    knowledgeBaseAssociationEnabled: {
      type: Boolean,
      default: true
    },
    imageGenEnabled: {
      type: Boolean,
      default: false
    },
    planEnabled: {
      type: Boolean,
      default: false
    },
    workspaceFiles: {
      type: Array,
      default: () => []
    },
    isSending: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      voiceState: 'idle',
      uploadingCount: 0,
      hasUrlAttachments: false,
      isMorphing: false,
      morphTimer: null,
      isCoreSelfUploadMode: false,
      nativeUploadTaskKey: '',
      nativeUploadPollTimer: null,
      nativeUploadUrlOverrides: Object.create(null),
      windowDragDepth: 0,
      windowDragLeaveTimer: null,
      activeDropTarget: '',
      showAttachmentMenu: false,
      attachmentMenuHideTimer: null,
      attachmentMenuOpenedAt: 0,
      attachmentMenuStyle: {},
      attachmentMenuPlacement: 'above',
      mentionOpen: false,
      mentionQuery: '',
      mentionIndex: 0,
      // Text offsets (in getEditorText() space) of the "@query" being typed.
      mentionRange: null
    };
  },
  computed: {
    filteredCompanions() {
      const query = (this.mentionQuery || '').trim().toLocaleLowerCase();
      const companions = Array.isArray(this.availableCompanions) ? this.availableCompanions : [];

      // With a bare "@", surface the user's most recently used companions
      // first.  The store keeps this list newest-first; entries that no
      // longer exist (for example, a deleted custom companion) are skipped,
      // and the remaining companions retain their normal default order.
      if (!query) {
        const companionMap = new Map(companions.map(companion => [String(companion.id), companion]));
        const recentIds = (store.companionSettings && store.companionSettings.companionHistory)
          || (store.roleSettings && store.roleSettings.roleHistory)
          || [];
        const recent = [];
        const recentSet = new Set();
        recentIds.forEach(id => {
          const key = String(id);
          const companion = companionMap.get(key);
          if (companion && !recentSet.has(key)) {
            recent.push(companion);
            recentSet.add(key);
          }
        });
        return recent.concat(companions.filter(companion => !recentSet.has(String(companion.id)))).slice(0, 8);
      }

      const score = (companion) => {
        const name = String(companion.name || '').toLocaleLowerCase();
        const namePinyin = this.getPinyinSearchForms(companion.name);
        const pinyinQuery = this.normalizePinyinQuery(query);
        const aliases = (companion.aliases || []).map(item => String(item).toLocaleLowerCase());
        const tags = (companion.tags || []).map(item => String(item).toLocaleLowerCase());
        const description = String(companion.description || '').toLocaleLowerCase();
        const secondaryPinyin = [...aliases, ...tags, description].some(item => this.pinyinMatches(item, pinyinQuery));
        if (name === query) return 0;
        if (name.startsWith(query)) return 1;
        if (namePinyin.full === pinyinQuery) return 2;
        if (namePinyin.full.startsWith(pinyinQuery)) return 3;
        if (namePinyin.initials === pinyinQuery) return 4;
        if (namePinyin.initials.startsWith(pinyinQuery)) return 5;
        if (secondaryPinyin) return 6;
        if (aliases.some(item => item === query)) return 7;
        if (aliases.some(item => item.includes(query))) return 8;
        if (tags.some(item => item.includes(query))) return 9;
        if (description.includes(query)) return 10;
        return -1;
      };
      return companions
        .map((companion, index) => ({ companion, index, rank: score(companion) }))
        .filter(item => item.rank >= 0)
        .sort((a, b) => a.rank - b.rank || a.index - b.index)
        .map(item => item.companion)
        .slice(0, 8);
    },
    cloudUpdatePending() {
      return store.cloudUpdatePending;
    },
    imageAspectRatio() {
      return store.settings.imageAspectRatio || '1:1';
    },
    imageSize() {
      return store.settings.imageSize || '1K';
    },
    commonInstructions() {
      const localeMessages = this.$i18n.getLocaleMessage(this.$i18n.locale) || {};
      const localized = localeMessages.chatInput && localeMessages.chatInput.instructions;
      return Array.isArray(localized) && localized.length > 0
        ? localized
          : [this.$tl('帮我解答'), this.$tl('这是什么')];
    },
    isVoiceUiActive() {
      return this.voiceState !== 'idle';
    },
    voiceButtonTitle() {
      return this.voiceState === 'recognizing' ? this.$tl('正在识别语音') : this.$tl('按住说话');
    }
  },
  watch: {
    isDragging(dragging) {
      if (!dragging) this.activeDropTarget = '';
    },
    inputText() {
      this.syncEditorContent();
    },
    activeCompanions: {
      deep: true,
      handler() {
        this.syncEditorContent();
      }
    },
    showAttachmentMenu(open) {
      // Dismiss on clicks/taps outside the attachment menu (capture phase so
      // handlers that stop propagation elsewhere can't swallow it).
      if (open) {
        document.addEventListener('click', this.closeAttachmentMenuOnOutsideClick, true);
      } else {
        document.removeEventListener('click', this.closeAttachmentMenuOnOutsideClick, true);
      }
    },
    isCollapsed(collapsed) {
      if (!this.isMobile) return;
      this.animateMorph(collapsed);
    }
  },
  beforeDestroy() {
    this.removeWindowDragListeners();
    if (this.morphTimer) {
      clearTimeout(this.morphTimer);
      this.morphTimer = null;
    }
    this.closeAttachmentMenu();
    this.clearNativeUploadPolling();
    if (this.holdToTalk) {
      this.holdToTalk.destroy();
      this.holdToTalk = null;
    }
    speechService.stopListening();
    speechService.stopSpeaking();
  },
  mounted() {
    this.holdToTalk = new HoldToTalk({
      translate: (text, params) => this.$tl(text, params),
      onStateChange: (state) => {
        this.voiceState = state;
      },
      onResult: (text) => this.applyRecognizedVoiceText(text)
    });
    this.addWindowDragListeners();
    this.syncUploadEnvironmentFromQuery();
    this.processUrlAttachments();
    this.$nextTick(() => {
      this.syncEditorContent();
    });
  },
  methods: {
    focusEditor() {
      if (this.$refs.textarea && typeof this.$refs.textarea.focus === 'function') {
        this.$refs.textarea.focus();
      }
    },
    getPinyinSearchForms(value) {
      const text = String(value || '');
      if (!text) return { syllables: [], full: '', initials: '' };
      const syllables = pinyin(text, { toneType: 'none', type: 'array' })
        .map(item => String(item || '').toLocaleLowerCase());
      return {
        syllables,
        full: syllables.join(''),
        initials: syllables.map(item => item.charAt(0)).join('')
      };
    },
    pinyinMatches(value, query) {
      const normalizedQuery = this.normalizePinyinQuery(query);
      if (!normalizedQuery) return false;
      const forms = this.getPinyinSearchForms(value);
      return forms.full.includes(normalizedQuery) || forms.initials.includes(normalizedQuery);
    },
    normalizePinyinQuery(value) {
      return String(value || '').toLocaleLowerCase().replace(/\s+/g, '');
    },
    highlightCompanionName(name) {
      const text = String(name || '');
      const query = String(this.mentionQuery || '').trim();
      if (!query) return this.escapeEditorHtml(text);
      const normalizedQuery = query.toLocaleLowerCase();
      const normalizedPinyinQuery = this.normalizePinyinQuery(query);
      const directQuery = this.escapeEditorHtml(query).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const escapedText = this.escapeEditorHtml(text);
      if (directQuery && text.toLocaleLowerCase().includes(normalizedQuery)) {
        return escapedText.replace(new RegExp(directQuery, 'ig'), match => `<mark class="mention-match">${match}</mark>`);
      }

      const forms = this.getPinyinSearchForms(text);
      const fullIndex = forms.full.indexOf(normalizedPinyinQuery);
      if (fullIndex !== -1 && normalizedPinyinQuery) {
        const start = this.pinyinCharIndex(forms.syllables, fullIndex);
        const end = this.pinyinCharIndex(forms.syllables, fullIndex + normalizedPinyinQuery.length - 1);
        if (start >= 0 && end >= start) return this.highlightNameRange(text, start, end);
      }

      const initialsIndex = forms.initials.indexOf(normalizedPinyinQuery);
      if (initialsIndex !== -1 && normalizedPinyinQuery) {
        return this.highlightNameRange(text, initialsIndex, initialsIndex + normalizedPinyinQuery.length - 1);
      }
      return escapedText;
    },
    pinyinCharIndex(syllables, pinyinOffset) {
      let offset = 0;
      for (let index = 0; index < syllables.length; index += 1) {
        const nextOffset = offset + syllables[index].length;
        if (pinyinOffset < nextOffset) return index;
        offset = nextOffset;
      }
      return -1;
    },
    highlightNameRange(text, start, end) {
      const chars = Array.from(String(text || ''));
      return chars.map((char, index) => {
        const safeChar = this.escapeEditorHtml(char);
        return index >= start && index <= end ? `<mark class="mention-match">${safeChar}</mark>` : safeChar;
      }).join('');
    },
    workspaceFileType(file) {
      const kind = String((file && (file.kind || file.mime)) || '').toLowerCase();
      const name = String((file && file.name) || '').toLowerCase();
      if (kind.includes('image') || /\.(png|jpe?g|gif|webp|svg)$/.test(name)) return 'IMG';
      if (kind.includes('sheet') || kind.includes('excel') || /\.(xlsx?|csv)$/.test(name)) return 'XLS';
      if (kind.includes('pdf') || /\.pdf$/.test(name)) return 'PDF';
      if (kind.includes('word') || kind.includes('document') || /\.(docx?|rtf)$/.test(name)) return 'DOC';
      return 'TXT';
    },
    hasDraggedFiles(event) {
      const types = event && event.dataTransfer && event.dataTransfer.types;
      return !!types && Array.from(types).some(type => type === 'Files' || type === 'application/x-moz-file');
    },
    addWindowDragListeners() {
      window.addEventListener('dragenter', this.handleWindowDragEnter, true);
      window.addEventListener('dragover', this.handleWindowDragOver, true);
      window.addEventListener('dragleave', this.handleWindowDragLeave, true);
      window.addEventListener('drop', this.handleWindowDrop, true);
    },
    removeWindowDragListeners() {
      window.removeEventListener('dragenter', this.handleWindowDragEnter, true);
      window.removeEventListener('dragover', this.handleWindowDragOver, true);
      window.removeEventListener('dragleave', this.handleWindowDragLeave, true);
      window.removeEventListener('drop', this.handleWindowDrop, true);
      this.windowDragDepth = 0;
      if (this.windowDragLeaveTimer) {
        clearTimeout(this.windowDragLeaveTimer);
        this.windowDragLeaveTimer = null;
      }
    },
    handleWindowDragEnter(event) {
      if (!this.hasDraggedFiles(event)) return;
      event.preventDefault();
      if (this.windowDragLeaveTimer) {
        clearTimeout(this.windowDragLeaveTimer);
        this.windowDragLeaveTimer = null;
      }
      this.windowDragDepth += 1;
      this.$emit('update:is-dragging', true);
    },
    handleWindowDragOver(event) {
      if (!this.hasDraggedFiles(event)) return;
      event.preventDefault();
      if (this.windowDragLeaveTimer) {
        clearTimeout(this.windowDragLeaveTimer);
        this.windowDragLeaveTimer = null;
      }
      this.$emit('update:is-dragging', true);
    },
    handleWindowDragLeave(event) {
      if (!this.hasDraggedFiles(event)) return;
      event.preventDefault();
      // Browsers fire dragleave while moving between the overlay and its
      // children. Keep the overlay open as long as the pointer is still in
      // the document; only close it when the drag leaves the viewport.
      const relatedTarget = event.relatedTarget;
      const insideDocument = relatedTarget && relatedTarget.nodeType === 1 &&
        document.documentElement.contains(relatedTarget);
      if (insideDocument) return;
      if (this.windowDragLeaveTimer) clearTimeout(this.windowDragLeaveTimer);
      this.windowDragLeaveTimer = setTimeout(() => {
        this.windowDragDepth = 0;
        this.$emit('update:is-dragging', false);
        this.windowDragLeaveTimer = null;
      }, 120);
    },
    handleWindowDrop(event) {
      if (!this.hasDraggedFiles(event)) return;
      // Let the actual drop target process the files. This listener runs in
      // capture phase, so clearing isDragging here would remove the target
      // before its Vue drop handler can run.
      const target = event.target;
      if (target && typeof target.closest === 'function' &&
        target.closest('.drag-target, .drag-overlay, .input-area')) {
        return;
      }
      if (this.windowDragLeaveTimer) {
        clearTimeout(this.windowDragLeaveTimer);
        this.windowDragLeaveTimer = null;
      }
      this.windowDragDepth = 0;
      this.$emit('update:is-dragging', false);
    },
    handleWrapperClick() {
      // Tapping the collapsed pill expands it back and focuses the input
      if (this.isMobile && this.isCollapsed) {
        this.$emit('update:is-collapsed', false);
      }
    },
    // Animate the wrapper morphing between full input and the minimal pill.
    // Height can't transition from `auto`, so we measure both ends explicitly.
    animateMorph(collapsed) {
      const el = this.$refs.inputWrapper;
      if (!el) return;

      if (this.morphTimer) {
        clearTimeout(this.morphTimer);
        this.morphTimer = null;
      }

      const startHeight = el.getBoundingClientRect().height;
      this.isMorphing = true;

      this.$nextTick(() => {
        // The `is-collapsed` class is now applied (reactive on isCollapsed),
        // so measuring with height:auto gives the true target size.
        el.style.height = 'auto';
        const targetHeight = el.getBoundingClientRect().height;

        // Reset to the start height, commit it, then transition to the target
        el.style.height = `${startHeight}px`;
        void el.offsetHeight;

        requestAnimationFrame(() => {
          el.style.height = `${targetHeight}px`;
        });
      });

      this.morphTimer = setTimeout(() => {
        this.isMorphing = false;
        el.style.height = '';
        this.morphTimer = null;
        if (!collapsed && this.$refs.textarea) {
          // Expanded: focus the input for immediate typing
          this.$refs.textarea.focus();
          this.adjustHeight({ target: this.$refs.textarea });
        }
      }, 360);
    },
    syncUploadEnvironmentFromQuery() {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        this.isCoreSelfUploadMode = String(urlParams.get('coreself') || '').toLowerCase() === 'true';
      } catch (error) {
        console.warn('Failed to parse coreself parameter:', error);
        this.isCoreSelfUploadMode = false;
      }
    },
    beginUploading() {
      this.uploadingCount += 1;
      this.$emit('update:is-uploading', this.uploadingCount > 0);
    },
    finishUploading() {
      this.uploadingCount = Math.max(0, this.uploadingCount - 1);
      this.$emit('update:is-uploading', this.uploadingCount > 0);
    },
    clearNativeUploadPolling() {
      if (this.nativeUploadPollTimer) {
        window.clearTimeout(this.nativeUploadPollTimer);
        this.nativeUploadPollTimer = null;
      }
    },
    // Voice input is WeChat-style hold-to-talk, implemented by HoldToTalk in utils/speech.js.
    handleVoicePointerDown(event) {
      if (this.holdToTalk) this.holdToTalk.handlePointerDown(event);
    },
    handleVoiceKeyDown(event) {
      if (this.holdToTalk) this.holdToTalk.handleKeyDown(event);
    },
    applyRecognizedVoiceText(text) {
      this.$emit('update:input-text', mergeSpeechText(this.inputText, text));
      this.focusTextarea();
    },
    focusTextarea() {
      this.$nextTick(() => {
        if (this.$refs.textarea) {
          this.$refs.textarea.focus();
          this.adjustHeight({ target: this.$refs.textarea });
        }
      });
    },
    notifyUploadFailure(message) {
      mutations.addMessage({
        role: 'assistant',
        content: message || this.$t('chatInput.fileUploadFailed'),
        timestamp: Date.now()
      });
      this.$emit('scroll-to-bottom', true);
      mutations.finalizeLastMessage();
    },
    extractUploadUrl(rawResult) {
      if (typeof rawResult !== 'string') {
        return '';
      }

      const trimmed = rawResult.trim();
      if (!trimmed) {
        return '';
      }

      try {
        const json = JSON.parse(trimmed);
        return (
          json.cdn_url ||
          json.fileurl ||
          json.url ||
          (json.data && (json.data.url || json.data.fileurl)) ||
          json.link ||
          ''
        );
      } catch (error) {
        return trimmed.startsWith('http') ? trimmed : '';
      }
    },
    deriveFileNameFromUrl(url, fallback = 'file') {
      if (!url) {
        return fallback;
      }

      try {
        const parsedUrl = new URL(url);
        const pathname = parsedUrl.pathname || '';
        return decodeURIComponent(pathname.split('/').pop() || fallback);
      } catch (error) {
        const pathname = url.split('?')[0] || '';
        return pathname.split('/').pop() || fallback;
      }
    },
    isImageLikeFile(file, fileUrl = '') {
      const imageExtRegex = /\.(jpg|jpeg|png|gif|bmp|webp|svg|ico)(\?|$)/i;
      if (file && typeof file.type === 'string' && file.type.startsWith('image/')) {
        return true;
      }

      const candidateName = (file && file.name) || this.deriveFileNameFromUrl(fileUrl, '');
      return imageExtRegex.test(candidateName || '');
    },
    buildNativeUploadFileKey(file) {
      return [file.name || '', file.size || 0, file.type || ''].join('::');
    },
    invokeNativeProtocol(action, params = {}) {
      try {
        const urlParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          urlParams.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
        });

        const queryString = urlParams.toString();
        const url = `aihelp://${action}${queryString ? '?' + queryString : ''}`;
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = url;
        document.body.appendChild(iframe);

        window.setTimeout(() => {
          if (iframe.parentNode) {
            iframe.parentNode.removeChild(iframe);
          }
        }, 500);
      } catch (error) {
        console.error('Failed to invoke native protocol:', error);
      }
    },
    openAttachmentMenu() {
      if (this.attachmentMenuHideTimer) {
        clearTimeout(this.attachmentMenuHideTimer);
        this.attachmentMenuHideTimer = null;
      }
      if (!this.showAttachmentMenu) {
        this.showAttachmentMenu = true;
        this.attachmentMenuOpenedAt = Date.now();
        this.$nextTick(this.positionAttachmentMenu);
      } else {
        this.positionAttachmentMenu();
      }
    },
    handleAttachmentWrapPointerEnter(e) {
      // Hover-open only for a real mouse. Touch taps also synthesize hover
      // events, and letting them open the menu races the click handler.
      if (e && e.pointerType && e.pointerType !== 'mouse') return;
      this.openAttachmentMenu();
    },
    handleAttachmentWrapPointerLeave(e) {
      if (e && e.pointerType && e.pointerType !== 'mouse') return;
      this.scheduleAttachmentMenuClose();
    },
    handleAttachmentMenuPointerEnter(e) {
      if (e && e.pointerType && e.pointerType !== 'mouse') return;
      this.cancelAttachmentMenuClose();
    },
    handleAttachmentMenuPointerLeave(e) {
      if (e && e.pointerType && e.pointerType !== 'mouse') return;
      this.scheduleAttachmentMenuClose();
    },
    handleAttachmentButtonClick() {
      if (!this.showAttachmentMenu) {
        this.openAttachmentMenu();
        return;
      }
      // Touch taps fire compatibility mouseenter/focusin before the click, so
      // the menu was just opened by this same gesture — keep it open instead
      // of toggling it straight back closed.
      if (Date.now() - this.attachmentMenuOpenedAt < 500) return;
      this.closeAttachmentMenu();
    },
    positionAttachmentMenu() {
      // The menu is positioned relative to the attachment wrapper. Keeping
      // it in that containing block avoids clipping from transformed inputs.
      this.attachmentMenuStyle = {};
    },
    cancelAttachmentMenuClose() {
      if (this.attachmentMenuHideTimer) {
        clearTimeout(this.attachmentMenuHideTimer);
        this.attachmentMenuHideTimer = null;
      }
    },
    scheduleAttachmentMenuClose() {
      this.cancelAttachmentMenuClose();
      this.attachmentMenuHideTimer = setTimeout(() => {
        this.showAttachmentMenu = false;
        this.attachmentMenuHideTimer = null;
      }, 160);
    },
    closeAttachmentMenu() {
      this.cancelAttachmentMenuClose();
      this.showAttachmentMenu = false;
    },
    closeAttachmentMenuOnOutsideClick(e) {
      const wrap = this.$refs.attachmentMenuWrap;
      if (wrap && (wrap === e.target || wrap.contains(e.target))) return;
      this.closeAttachmentMenu();
    },
    selectAttachmentSource(source) {
      this.closeAttachmentMenu();
      if (source === 'workspace') {
        if (this.$refs.workspaceFileInput) {
          this.$refs.workspaceFileInput.click();
        }
        return;
      }
      this.handleAttachmentClick();
    },
    handleWorkspaceFilePick(e) {
      const input = e && e.target;
      const files = Array.from((input && input.files) || []);
      if (input) input.value = '';
      if (files.length > 0) this.$emit('workspace-drop', files);
    },
    async handleAttachmentClick() {
   
      if (!this.isCoreSelfUploadMode) {
        if (this.$refs.fileInput) {
          this.$refs.fileInput.click();
        }
        return;
      }

      await this.startNativeUploadFlow();
    },
    async createNativeUploadTask() {
      const response = await fetch('https://wm.ktllq.cn/Aibrowser/createUploadTask', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          source: 'chat_attachment'
        })
      });
      const result = await response.json();

      if (result.code !== 200 || !result.data || !result.data.key) {
        throw new Error((result && (result.msg || result.message)) || 'Failed to create upload task');
      }

      return result.data.key;
    },
    async fetchNativeUploadTask(uploadKey) {
      const response = await fetch(`https://wm.ktllq.cn/Aibrowser/getUploadTask?key=${encodeURIComponent(uploadKey)}`);
      const result = await response.json();

      if (result.code !== 200 || !result.data) {
        throw new Error((result && (result.msg || result.message)) || 'Failed to fetch upload task');
      }

      return result.data;
    },
    pollNativeUploadTask(uploadKey) {
      const maxAttempts = 12;
      const intervalMs = 5000;
      let attempts = 0;

      return new Promise((resolve, reject) => {
        const poll = async () => {
          attempts += 1;

          try {
            const taskData = await this.fetchNativeUploadTask(uploadKey);
            const status = String(taskData.status || 'pending').toLowerCase();

            if (status === 'completed' && taskData.file && taskData.file.url) {
              this.clearNativeUploadPolling();
              resolve(taskData.file);
              return;
            }

            if (status === 'failed') {
              this.clearNativeUploadPolling();
              reject(new Error(taskData.error || 'NATIVE_UPLOAD_FAILED'));
              return;
            }

            if (status === 'cancelled' || status === 'canceled') {
              this.clearNativeUploadPolling();
              reject(new Error('NATIVE_UPLOAD_CANCELLED'));
              return;
            }
          } catch (error) {
            if (attempts >= maxAttempts) {
              this.clearNativeUploadPolling();
              reject(error);
              return;
            }
          }

          if (attempts >= maxAttempts) {
            this.clearNativeUploadPolling();
            reject(new Error('NATIVE_UPLOAD_TIMEOUT'));
            return;
          }

          this.nativeUploadPollTimer = window.setTimeout(poll, intervalMs);
        };

        this.clearNativeUploadPolling();
        this.nativeUploadPollTimer = window.setTimeout(poll, intervalMs);
      });
    },
    async downloadNativeUploadedFile(fileInfo) {
      const fileUrl = String((fileInfo && fileInfo.url) || '').trim();
      if (!fileUrl) {
        throw new Error('Native upload result missing url');
      }

      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch uploaded file: ${response.status}`);
      }

      const blob = await response.blob();
      const fileName = fileInfo.name || this.deriveFileNameFromUrl(fileUrl);
      const mimeType = fileInfo.mime_type || fileInfo.mimeType || blob.type || '';
      return new File([blob], fileName, { type: mimeType });
    },
    async consumeNativeUploadedFile(fileInfo) {
      try {
        const file = await this.downloadNativeUploadedFile(fileInfo);
        const fileKey = this.buildNativeUploadFileKey(file);
        this.nativeUploadUrlOverrides[fileKey] = fileInfo.url;

        await this.handleFileUpload(
          {
            target: {
              files: [file],
              value: ''
            }
          },
          {
            manageUploadState: false
          }
        );
      } catch (error) {
        if (this.isImageLikeFile({ name: fileInfo.name || '', type: fileInfo.mime_type || '' }, fileInfo.url)) {
          const imageAttachment = {
            type: 'image',
            url: fileInfo.url,
            name: fileInfo.name || this.deriveFileNameFromUrl(fileInfo.url),
            summary: `Image attachment: ${fileInfo.name || this.deriveFileNameFromUrl(fileInfo.url)}`
          };

          await mutations.addAttachmentToPool(imageAttachment);
          this.$emit('add-attachment', imageAttachment);
          return;
        }

        throw error;
      }
    },
    async startNativeUploadFlow() {
      if (this.nativeUploadTaskKey) {
        return;
      }

      this.beginUploading();

      try {
        const uploadKey = await this.createNativeUploadTask();
        this.nativeUploadTaskKey = uploadKey;
        this.invokeNativeProtocol('uploadAttachment', { key: uploadKey });

        const fileInfo = await this.pollNativeUploadTask(uploadKey);
        await this.consumeNativeUploadedFile(fileInfo);
      } catch (error) {
        if (error && error.message === 'NATIVE_UPLOAD_CANCELLED') {
          return;
        }

        console.error('Native upload flow failed:', error);
        if (error && error.message === 'NATIVE_UPLOAD_TIMEOUT') {
          this.notifyUploadFailure(this.$tl('文件选择超时，请重试。'));
        } else {
          this.notifyUploadFailure(this.$t('chatInput.fileUploadFailed'));
        }
      } finally {
        this.clearNativeUploadPolling();
        this.nativeUploadTaskKey = '';
        this.finishUploading();
      }
    },
    applyInstruction(text) {
      this.$emit('update:input-text', text);
      this.$nextTick(() => {
        if (this.$refs.textarea) {
          this.$refs.textarea.focus();
          this.adjustHeight({ target: this.$refs.textarea });
        }
        // 点击后直接发送
        if (!this.isSending) {
          this.$emit('send-message');
        }
      });
    },
    processUrlAttachments() {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        let raw = urlParams.get('attachfileurls');
        if (!raw) return;

        // Check if the parameter value is base64 encoded
        // A simple heuristic: if it doesn't start with http but looks like it could be base64
        if (raw && !raw.startsWith('http') && !raw.includes(',')) {
          try {
            const decoded = atob(raw);
            if (decoded.includes('http')) {
              raw = decoded;
            }
          } catch (e) {
            // Not a valid base64 or doesn't contain a URL, ignore and use raw
          }
        }

        const urls = raw.split(',').map(u => u.trim()).filter(Boolean);
        if (urls.length === 0) return;

        const imageExtRegex = /\.(jpg|jpeg|png|gif|bmp|webp|svg|ico)(\?|$)/i;
        let added = 0;
        for (const url of urls) {
          if (!/^https?:\/\//i.test(url)) continue;
          const pathname = url.split('?')[0];
          let name = 'file';
          try {
            name = decodeURIComponent(pathname.split('/').pop() || 'file');
          } catch (_) {
            name = pathname.split('/').pop() || 'file';
          }
          if (imageExtRegex.test(pathname)) {
            const imageAttachment = {
              type: 'image',
              url,
              name,
              summary: `Image attachment: ${name}`
            };
            this.$emit('add-attachment', imageAttachment);
            mutations.addAttachmentToPool(imageAttachment).catch(e => {
              console.error('Failed to save image attachment to pool', e);
            });
          } else {
            this.$emit('add-attachment', { type: 'file', url, name, content: '', summary: '' });
          }
          added += 1;
        }

        if (added > 0) {
          this.hasUrlAttachments = true;
        }

        // Strip the param so refresh doesn't re-add the same files
    //    urlParams.delete('attachfileurls');
    //    const newSearch = urlParams.toString();
   //     const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash;
    //    window.history.replaceState({}, '', newUrl);
      } catch (e) {
        console.error('Failed to parse attachfileurls:', e);
      }
    },
    handleCloudAction(action) {
      mutations.handleCloudUpdate(action);
    },
    async processFile(file, options = {}) {
      if (!file) return;
      const { existingUrl = '', manageUploadState = true } = options;

      if (manageUploadState) {
        this.beginUploading();
      }
      try {
        // Check if image or document
        if (this.isImageLikeFile(file, existingUrl)) {
            let imageUrl = existingUrl.trim();
            if (!imageUrl) {
              const imageFormData = new FormData();
              imageFormData.append("file", file);

              const response = await fetch("//picmain.rainsee.cn/api.php", {
                method: 'POST',
                body: imageFormData,
                redirect: 'follow'
              });
              const result = await response.text();
              imageUrl = this.extractUploadUrl(result);
            }

            if (imageUrl && imageUrl.startsWith('http')) {
                 const normalizedImageUrl = imageUrl.trim();
                 await new Promise((resolve) => {
                   const img = new Image();
                   img.onload = resolve;
                   img.onerror = resolve;
                   img.src = normalizedImageUrl;
                 });

                 const imageAttachment = {
                   type: 'image',
                   url: normalizedImageUrl,
                   name: file.name,
                   summary: `Image attachment: ${file.name}`
                 };

                 await mutations.addAttachmentToPool(imageAttachment);
                 this.$emit('add-attachment', imageAttachment);
            } else {
                 console.error('Invalid upload response:', imageUrl);
                 this.notifyUploadFailure(this.$t('chatInput.uploadInvalidUrl'));
            }
        } else {
            // Handle document upload
            const contentFormData = new FormData();
            contentFormData.append("file", file);

            let fileUrl = existingUrl.trim();
            let contentResponse;

            if (fileUrl) {
              contentResponse = await fetch(MARKITDOWN_API_URL, {
                method: 'POST',
                body: contentFormData,
                redirect: 'follow'
              });
            } else {
              const urlFormData = new FormData();
              urlFormData.append("file", file);

              const [nextContentResponse, urlResponse] = await Promise.all([
                fetch(MARKITDOWN_API_URL, {
                  method: 'POST',
                  body: contentFormData,
                  redirect: 'follow'
                }),
                fetch("//picmain.rainsee.cn/api.php", {
                  method: 'POST',
                  body: urlFormData,
                  redirect: 'follow'
                })
              ]);

              contentResponse = nextContentResponse;
              fileUrl = this.extractUploadUrl(await urlResponse.text());
            }

            if (!contentResponse.ok) {
                throw new Error(`Upload failed: ${contentResponse.status} ${contentResponse.statusText}`);
            }
            
            const json = await contentResponse.json();
            
            if (json && json.markdown) {
                const compressedMarkdown = compressMarkdownTable(json.markdown);
                
                // Get summary for the document
                const summary = await summarizeAttachmentAsync(file.name, compressedMarkdown);
                
                // Add to global pool
                await mutations.addAttachmentToPool({
                  name: file.name,
                  url: fileUrl,
                  summary: summary,
                  type: 'file',
                  content: compressedMarkdown // Keep content for the current round
                });

                this.$emit('add-attachment', {
                   type: 'file',
                   content: compressedMarkdown,
                   url: fileUrl,
                   name: file.name,
                   summary: summary
                });
            } else {
                console.error('Invalid document response:', json);
                this.notifyUploadFailure(this.$t('chatInput.documentProcessingFailed'));
            }
        }

      } catch (error) {
        console.error('Upload error:', error);
        this.notifyUploadFailure(this.$t('chatInput.fileUploadFailed'));
      } finally {
        if (manageUploadState) {
          this.finishUploading();
        }
        // clear input value so the same file can be uploaded again if needed
        if (this.$refs.fileInput) {
            this.$refs.fileInput.value = '';
        }
      }
    },
    async handleFileUpload(event, options = {}) {
      const files = event.target.files || event.dataTransfer?.files;
      if (!files || files.length === 0) return;
      const shouldManageUploadState = options.manageUploadState !== false;
      
      for (const file of Array.from(files)) {
        const fileKey = this.buildNativeUploadFileKey(file);
        const existingUrl = this.nativeUploadUrlOverrides[fileKey] || '';

        if (existingUrl) {
          delete this.nativeUploadUrlOverrides[fileKey];
        }

        await this.processFile(file, {
          existingUrl,
          manageUploadState: shouldManageUploadState
        });
      }

      // Reset input value so the same file can be uploaded again if needed
      if (event.target.value) {
          event.target.value = '';
      }
    },
    handleInput(e) {
      const value = e.target.value;
      const mentionMatch = value.match(/(?:^|\s)@([^\s@]*)$/);
      if (mentionMatch) {
        this.mentionQuery = mentionMatch[1] || '';
        this.mentionIndex = 0;
        this.mentionOpen = true;
      } else {
        this.closeMention();
      }
      this.$emit('update:input-text', value);
      this.adjustHeight(e);
    },
    escapeEditorHtml(value) {
      return String(value || '').replace(/[&<>"']/g, char => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      }[char]));
    },
    readEditorNodeText(node) {
      if (node.nodeType === Node.TEXT_NODE) return node.nodeValue || '';
      if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.classList.contains('companion-token-remove')) return '';
        if (node.classList.contains('companion-inline-token')) return node.dataset.token || '';
      } else if (node.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) {
        return '';
      }
      return Array.from(node.childNodes).map(child => this.readEditorNodeText(child)).join('');
    },
    getEditorText() {
      const editor = this.$refs.textarea;
      if (!editor) return this.inputText || '';
      return this.readEditorNodeText(editor).replace(/\u00a0/g, '');
    },
    // Caret position measured in the same text space as getEditorText().
    getCaretTextOffset() {
      const editor = this.$refs.textarea;
      const selection = window.getSelection && window.getSelection();
      if (!editor || !selection || !selection.rangeCount) return null;
      const range = selection.getRangeAt(0);
      if (!editor.contains(range.endContainer)) return null;
      const before = document.createRange();
      before.selectNodeContents(editor);
      before.setEnd(range.endContainer, range.endOffset);
      return this.readEditorNodeText(before.cloneContents()).replace(/\u00a0/g, '').length;
    },
    setCaretAtTextOffset(offset) {
      const editor = this.$refs.textarea;
      const selection = window.getSelection && window.getSelection();
      if (!editor || !selection) return;
      const range = document.createRange();
      let remaining = Math.max(0, offset);
      const walk = parent => {
        for (const node of Array.from(parent.childNodes)) {
          if (node.nodeType === Node.TEXT_NODE) {
            const text = node.nodeValue || '';
            for (let i = 0; i <= text.length; i++) {
              if (remaining === 0) {
                range.setStart(node, i);
                return true;
              }
              // getEditorText() drops \u00a0, so it doesn't count toward the offset.
              if (i < text.length && text[i] !== '\u00a0') remaining--;
            }
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.classList.contains('companion-inline-token')) {
              if (remaining === 0) {
                range.setStartBefore(node);
                return true;
              }
              // The caret can't sit inside a token; snap to just after it.
              remaining = Math.max(0, remaining - (node.dataset.token || '').length);
              if (remaining === 0) {
                range.setStartAfter(node);
                return true;
              }
            } else if (walk(node)) {
              return true;
            }
          }
        }
        return false;
      };
      if (!walk(editor)) {
        range.selectNodeContents(editor);
        range.collapse(false);
      } else {
        range.collapse(true);
      }
      selection.removeAllRanges();
      selection.addRange(range);
    },
    // Detects an "@query" ending at the caret, anywhere in the text.
    // Returns the query, or null when there's no active mention.
    updateMentionState(value) {
      const caret = this.getCaretTextOffset();
      const end = caret === null ? value.length : Math.min(caret, value.length);
      // Any "@" triggers the picker, even when glued to preceding text.
      const match = value.slice(0, end).match(/@([^\s@]*)$/);
      if (!match) {
        this.closeMention();
        return null;
      }
      const query = match[1] || '';
      this.mentionQuery = query;
      this.mentionIndex = 0;
      this.mentionRange = { start: end - query.length - 1, end };
      return query;
    },
    renderEditorHtml(value = this.inputText) {
      let html = this.escapeEditorHtml(value || '');
      (this.activeCompanions || []).slice().sort((a, b) => String(b.name || '').length - String(a.name || '').length).forEach(companion => {
        const token = `@${companion.name}`;
        const safeToken = this.escapeEditorHtml(token);
        const safeId = this.escapeEditorHtml(companion.id);
        const tokenHtml = `<span class="companion-inline-token" contenteditable="false" data-token="${safeToken}" data-companion-id="${safeId}"><span class="companion-inline-label">${safeToken}</span><button type="button" class="companion-token-remove" data-companion-id="${safeId}" aria-label="移除伙伴">×</button></span>`;
        html = html.split(safeToken).join(tokenHtml);
      });
      return html || '';
    },
    syncEditorContent() {
      const editor = this.$refs.textarea;
      if (!editor) return;
      const expected = String(this.inputText || '');
      const tokensAreRendered = (this.activeCompanions || []).every(companion => {
        const token = editor.querySelector(`[data-companion-id="${String(companion.id).replace(/"/g, '\\"')}"]`);
        return !expected.includes(`@${companion.name}`) || !!token;
      });
      if (this.getEditorText() === expected && tokensAreRendered) {
        this.adjustHeight({ target: editor });
        return;
      }
      // Rewriting innerHTML drops the selection; keep the caret where it was.
      const caret = document.activeElement === editor ? this.getCaretTextOffset() : null;
      editor.innerHTML = this.renderEditorHtml(expected);
      if (caret !== null) this.setCaretAtTextOffset(caret);
      this.adjustHeight({ target: editor });
    },
    handleEditorInput(e) {
      const value = this.getEditorText();
      this.$emit('update:input-text', value);
      const query = this.updateMentionState(value);
      if (query !== null) {
        const normalizedQuery = query.toLocaleLowerCase();
        const exactCompanion = this.availableCompanions.find(companion => {
          const name = String(companion.name || '').toLocaleLowerCase();
          const aliases = (companion.aliases || []).map(alias => String(alias).toLocaleLowerCase());
          return name === normalizedQuery || aliases.includes(normalizedQuery);
        });
        if (exactCompanion && !(this.activeCompanions || []).some(item => item.id === exactCompanion.id)) {
          this.selectCompanionMention(exactCompanion, value);
        } else {
          this.mentionOpen = true;
        }
      }
      const activeIds = (this.activeCompanions || []).map(item => item.id);
      activeIds.forEach(id => {
        const companion = this.activeCompanions.find(item => item.id === id);
        if (companion && !value.includes(`@${companion.name}`)) {
          this.$emit('remove-companion', id);
        }
      });
      this.adjustHeight(e);
    },
    removeTokenAtCaret(direction) {
      const editor = this.$refs.textarea;
      const selection = window.getSelection && window.getSelection();
      if (!editor || !selection || !selection.rangeCount || !selection.isCollapsed) return false;

      const range = selection.getRangeAt(0);
      let node = range.startContainer;
      let offset = range.startOffset;
      let candidate = null;

      // A caret normally lives in a text node.  Only inspect a sibling token
      // when the caret is at that text node's edge, preserving normal text
      // editing semantics (for example, Backspace still removes a space first).
      if (node.nodeType === Node.TEXT_NODE) {
        if (direction === 'backward' && offset === 0) {
          candidate = node.previousSibling;
        } else if (direction === 'forward' && offset === node.nodeValue.length) {
          candidate = node.nextSibling;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const children = node.childNodes || [];
        if (direction === 'backward' && offset > 0) candidate = children[offset - 1];
        if (direction === 'forward' && offset < children.length) candidate = children[offset];
      }

      // Walk through an inline wrapper (or the editor itself) to find the
      // token immediately adjacent to the caret.
      while (candidate && candidate !== editor && candidate.nodeType === Node.ELEMENT_NODE && !candidate.classList.contains('companion-inline-token')) {
        candidate = direction === 'backward' ? candidate.lastChild : candidate.firstChild;
      }
      if (!candidate || candidate.nodeType !== Node.ELEMENT_NODE || !candidate.classList.contains('companion-inline-token')) {
        return false;
      }

      const id = candidate.dataset.companionId;
      candidate.remove();
      this.$emit('update:input-text', this.getEditorText());
      if (id) this.$emit('remove-companion', id);
      return true;
    },
    handleEditorKeydown(e) {
      if (e.key === 'Backspace' && this.removeTokenAtCaret('backward')) {
        e.preventDefault();
        return;
      }
      if (e.key === 'Delete' && this.removeTokenAtCaret('forward')) {
        e.preventDefault();
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.handleEnterKey();
        return;
      }
      if (e.key === 'ArrowUp' && this.mentionOpen) {
        e.preventDefault();
        this.moveMention(-1);
      } else if (e.key === 'ArrowDown' && this.mentionOpen) {
        e.preventDefault();
        this.moveMention(1);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.closeMention();
      } else if (this.mentionOpen && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
        // Moving the caret away invalidates the tracked "@query" range.
        this.closeMention();
      }
    },
    handleEditorClick(e) {
      const removeButton = e.target && e.target.closest
        ? e.target.closest('.companion-token-remove')
        : null;
      if (!removeButton) {
        this.closeMention();
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      const id = removeButton.dataset.companionId;
      const token = removeButton.closest('.companion-inline-token');
      if (token) token.remove();
      this.$emit('update:input-text', this.getEditorText());
      this.$emit('remove-companion', id);
      this.$nextTick(() => this.$refs.textarea && this.$refs.textarea.focus());
    },
    handleEnterKey() {
      if (this.mentionOpen) {
        const companion = this.filteredCompanions[this.mentionIndex];
        if (companion) {
          this.selectCompanionMention(companion);
          return;
        }
      }
      this.$emit('send-message');
    },
    moveMention(step) {
      if (!this.mentionOpen) return;
      const count = this.filteredCompanions.length;
      if (!count) return;
      this.mentionIndex = (this.mentionIndex + step + count) % count;
    },
    closeMention() {
      this.mentionOpen = false;
      this.mentionQuery = '';
      this.mentionIndex = 0;
      this.mentionRange = null;
    },
    selectCompanionMention(companion, sourceValue = null) {
      if (!companion) return;
      const source = String(sourceValue === null ? this.inputText : sourceValue) || '';
      const range = this.mentionRange;
      let before;
      let after;
      if (range && range.start >= 0 && range.end <= source.length && source[range.start] === '@') {
        // Replace the "@query" in place, keeping text after the caret.
        before = source.slice(0, range.start);
        after = source.slice(range.end);
      } else {
        before = source.replace(/@[^\s@]*$/, '');
        after = '';
      }
      before = before.replace(/\s+$/, '');
      const head = `${before ? `${before} ` : ''}@${companion.name} `;
      const nextText = head + after.replace(/^\s+/, '');
      const caretOffset = head.length;
      this.$emit('update:input-text', nextText);
      this.$emit('add-companion', companion);
      this.closeMention();
      this.$nextTick(() => {
        if (this.$refs.textarea) {
          this.$refs.textarea.focus();
          this.setCaretAtTextOffset(caretOffset);
        }
      });
    },
    adjustHeight(e) {
      const el = e.target;
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 150) + 'px';
    },
    resetHeight() {
      this.$nextTick(() => {
        if (this.$refs.textarea) {
          this.$refs.textarea.style.height = 'auto';
          this.adjustHeight({ target: this.$refs.textarea });
        }
      });
    },
    handleDragOver(e) {
      if (!this.hasDraggedFiles(e)) return;
      e.preventDefault();
      this.$emit('update:is-dragging', true);
    },
    handleDragLeave(e) {
      if (!this.hasDraggedFiles(e)) return;
      e.preventDefault();
      // The window listener owns the global drag lifecycle. The input can
      // receive a synthetic dragleave when the full-screen overlay mounts,
      // so it must not hide that overlay itself.
    },
    handleTargetDragOver(e, target) {
      if (!this.hasDraggedFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      if (this.windowDragLeaveTimer) {
        clearTimeout(this.windowDragLeaveTimer);
        this.windowDragLeaveTimer = null;
      }
      this.activeDropTarget = target;
      this.$emit('update:is-dragging', true);
    },
    handleTargetDragLeave(e, target) {
      if (!this.hasDraggedFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      // Moving onto the target's own icon/text also fires dragleave; only
      // clear the highlight once the pointer actually leaves the card.
      const related = e.relatedTarget;
      if (related && e.currentTarget.contains(related)) return;
      if (this.activeDropTarget === target) this.activeDropTarget = '';
    },
    async handleAttachmentDrop(e) {
      e.preventDefault();
      e.stopPropagation();
      if (this.windowDragLeaveTimer) {
        clearTimeout(this.windowDragLeaveTimer);
        this.windowDragLeaveTimer = null;
      }
      this.windowDragDepth = 0;
      this.$emit('update:is-dragging', false);
      const files = Array.from(e.dataTransfer.files || []);
      for (const file of files) {
        await this.processFile(file);
      }
    },
    handleWorkspaceDrop(e) {
      e.preventDefault();
      e.stopPropagation();
      if (this.windowDragLeaveTimer) {
        clearTimeout(this.windowDragLeaveTimer);
        this.windowDragLeaveTimer = null;
      }
      this.windowDragDepth = 0;
      this.$emit('update:is-dragging', false);
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length > 0) this.$emit('workspace-drop', files);
    },
    async handleDrop(e) {
      e.preventDefault();
      e.stopPropagation();
      if (this.windowDragLeaveTimer) {
        clearTimeout(this.windowDragLeaveTimer);
        this.windowDragLeaveTimer = null;
      }
      this.windowDragDepth = 0;
      this.$emit('update:is-dragging', false);
 
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        for (const file of files) {
          await this.processFile(file);
        }
      }
    },
    async handlePaste(e) {
      const clipboardData = e.clipboardData || (e.originalEvent && e.originalEvent.clipboardData);
      if (!clipboardData) return;

      // Own the paste so contenteditable cannot insert clipboard HTML or image
      // elements directly into the editor.
      e.preventDefault();

      const items = Array.from(clipboardData.items || []);
      const files = [];
      for (const item of items) {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }

      // Image clipboard payloads can also expose an alt-text/plain-text flavor;
      // treat an image paste only as an attachment.
      const containsImage = files.some(file => String(file.type || '').startsWith('image/'));

      if (!containsImage) {
        const text = clipboardData.getData('text/plain');
        const editor = this.$refs.textarea;
        if (text && editor) {
          // insertText keeps the caret position and editor's native undo history,
          // while inserting clipboard text without its rich-text formatting.
          if (!document.execCommand || !document.execCommand('insertText', false, text)) {
            // Fallback for engines without insertText support.
            editor.focus();
            const selection = window.getSelection && window.getSelection();
            if (selection && selection.rangeCount) {
              const range = selection.getRangeAt(0);
              if (editor.contains(range.startContainer)) {
                range.deleteContents();
                const textNode = document.createTextNode(text);
                range.insertNode(textNode);
                range.setStartAfter(textNode);
                range.collapse(true);
                selection.removeAllRanges();
                selection.addRange(range);
                const inputEvent = typeof InputEvent === 'function'
                  ? new InputEvent('input', { bubbles: true, inputType: 'insertFromPaste', data: text })
                  : new Event('input', { bubbles: true });
                editor.dispatchEvent(inputEvent);
              }
            }
          }
        }
      }

      // Upload after inserting text so a slow attachment upload cannot move the
      // editor selection before the pasted text is inserted.
      for (const file of files) {
        await this.processFile(file);
      }
    },
    openFile(url) {
      if (url && url.startsWith('http')) {
        window.open(url, '_blank');
      }
    },
    handleThinkingToggle() {
      console.log("handleThinkingToggle");
      this.$emit('update:thinking-enabled', !this.thinkingEnabled);
    },
    handleWebSearchToggle() {
      this.$emit('update:web-search-enabled', !this.webSearchEnabled);
    },
    handleKnowledgeBaseAssociationToggle() {
      // 激活知识库关联前先校验登录，未登录则提示并跳转登录
      if (!this.knowledgeBaseAssociationEnabled && !store.token) {
        showToast(this.$tl('请先登录'), 'warning');
        setTimeout(() => this.$emit('open-login'), 900);
        return;
      }
      this.$emit('update:knowledge-base-association-enabled', !this.knowledgeBaseAssociationEnabled);
    },
    handleImageGenToggle() {
      this.$emit('update:image-gen-enabled', !this.imageGenEnabled);
    },
    updateImageConfig(key, value) {
      if (key === 'aspectRatio') mutations.setImageAspectRatio(value);
      if (key === 'size') mutations.setImageSize(value);
    },
    handlePlanToggle() {
      this.$emit('update:plan-enabled', !this.planEnabled);
    }
  }
};
</script>

<style scoped>
/* Input Area - Floating Surface */
.input-area {
  position: absolute;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 768px;
  z-index: 20;
  transition: all 0.3s ease;
}

.input-area.dragging {
  transform: translateX(-50%) scale(1.02);
}

/* ============================================================
 * Collapse <-> expand morph (mobile).
 * The input-wrapper itself shrinks: width, height, padding and
 * radius all transition down to a minimal pill anchored bottom-center.
 * Inner content fades out; the collapsed search overlay fades in.
 * ============================================================ */

/* Height is driven inline by JS; add it to the transition while morphing */
.input-wrapper.is-morphing {
  transition: height 0.36s cubic-bezier(0.22, 1, 0.36, 1),
              max-width 0.36s cubic-bezier(0.22, 1, 0.36, 1),
              padding 0.36s cubic-bezier(0.22, 1, 0.36, 1),
              border-radius 0.36s cubic-bezier(0.22, 1, 0.36, 1),
              background 0.3s ease,
              box-shadow 0.36s cubic-bezier(0.22, 1, 0.36, 1);
  overflow: hidden;
}

/* Collapsed search overlay: sits on top of the inner content */
.collapsed-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 0 16px;
  color: #8A8A8E;
  font-size: 13px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.22s ease;
}

.collapsed-overlay svg {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  color: #4A65E6;
}

[data-theme="dark"] .collapsed-overlay {
  color: #98989d;
}

[data-theme="dark"] .collapsed-overlay svg {
  color: #6E88F9;
}

.collapsed-overlay-text {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Inner content wrapper: fades/scales away when collapsing */
.input-inner {
  transition: opacity 0.2s ease, transform 0.28s cubic-bezier(0.22, 1, 0.36, 1);
  transform-origin: bottom center;
}

/* --- Collapsed state --- */
.input-wrapper.is-collapsed {
  width: 62vw;
  max-width: 320px;
  min-height: 40px;
  padding: 0 16px;
  border-radius: 999px;
  cursor: pointer;
  background: rgba(251, 251, 253, 0.6);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  backdrop-filter: blur(20px) saturate(180%);
  border-color: rgba(255, 255, 255, 0.5);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.14), 0 2px 6px rgba(15, 23, 42, 0.06);
  /* keep it centered horizontally */
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 20px;
  justify-content: center;
  overflow: hidden;
}

[data-theme="dark"] .input-wrapper.is-collapsed {
  background: rgba(23, 26, 33, 0.6);
  border-color: rgba(148, 163, 184, 0.18);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
}

.input-wrapper.is-collapsed .input-inner {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: auto;
  padding: 14px 18px 18px;
  opacity: 0;
  transform: translateY(8px) scale(0.96);
  pointer-events: none;
}

.input-wrapper.is-collapsed .collapsed-overlay {
  opacity: 1;
  pointer-events: auto;
  /* slight delay so it appears as the box finishes shrinking */
  transition: opacity 0.24s ease 0.1s;
}

.conversation-tip {
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  padding: 6px 12px;
  margin-bottom: 8px;
  font-size: 13px;
  color: var(--text-secondary);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03);
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
}

[data-theme="dark"] .conversation-tip {
  background-color: rgba(45, 45, 45, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.6);
}

.tip-link {
  color: #3b82f6;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 500;
  transition: opacity 0.2s;
}

.tip-link:hover {
  opacity: 0.8;
}

.cloud-update-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: fit-content;
  margin: 0 auto 12px;
  padding: 6px 8px 6px 12px;
  background: rgba(255, 241, 242, 0.8);
  border: 1px solid rgba(254, 205, 211, 0.5);
  border-radius: 100px;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 4px 12px rgba(225, 29, 72, 0.08);
  gap: 16px;
}

[data-theme="dark"] .cloud-update-banner {
  background: rgba(69, 10, 10, 0.6);
  border-color: rgba(153, 27, 27, 0.4);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.banner-content {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #e11d48;
}

[data-theme="dark"] .banner-content {
  color: #fda4af;
}

.banner-icon {
  stroke: currentColor;
  margin-top: -1px;
}

.banner-text {
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.2px;
}

.banner-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.banner-btn {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 100px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.banner-btn.primary {
  background: #e11d48;
  color: white;
  box-shadow: 0 2px 6px rgba(225, 29, 72, 0.2);
}

.banner-btn.primary:hover {
  background: #be123c;
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(225, 29, 72, 0.3);
}

.banner-btn.secondary {
  background: transparent;
  color: #e11d48;
  border: 1px solid rgba(225, 29, 72, 0.3);
}

.banner-btn.secondary:hover {
  background: rgba(225, 29, 72, 0.08);
}

[data-theme="dark"] .banner-btn.primary {
  background: #f43f5e;
  color: #fff;
}

[data-theme="dark"] .banner-btn.primary:hover {
  background: #fb7185;
}

[data-theme="dark"] .banner-btn.secondary {
  color: #fda4af;
  border-color: rgba(253, 164, 175, 0.3);
}

[data-theme="dark"] .banner-btn.secondary:hover {
  background: rgba(253, 164, 175, 0.1);
}

.disabled-btn {
  opacity: 0.5;
  cursor: not-allowed !important;
}

.input-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  background: #FBFBFD;
  border-radius: 28px;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08), 0 2px 8px rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(229, 231, 235, 0.9);
  overflow: visible;
  transform-origin: bottom center;
  transition: border-color 0.2s,
              background 0.3s ease,
              max-width 0.36s cubic-bezier(0.22, 1, 0.36, 1),
              padding 0.36s cubic-bezier(0.22, 1, 0.36, 1),
              border-radius 0.36s cubic-bezier(0.22, 1, 0.36, 1),
              box-shadow 0.36s cubic-bezier(0.22, 1, 0.36, 1);
  padding: 14px 18px 18px;
}

[data-theme="dark"] .input-wrapper {
  background: #171A21;
  border-color: rgba(148, 163, 184, 0.14);
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.38);
}

.input-wrapper:focus-within {
  border-color: #4A65E6;
  box-shadow: 0 20px 50px rgba(74, 101, 230, 0.12), 0 2px 8px rgba(15, 23, 42, 0.04);
}

[data-theme="dark"] .input-wrapper.voice-mode-active,
.input-wrapper.voice-mode-active {
  border-color: rgba(16, 185, 129, 0.28);
}

.input-wrapper.voice-mode-active {
  box-shadow: 0 20px 50px rgba(16, 185, 129, 0.12), 0 2px 8px rgba(15, 23, 42, 0.04);
}

[data-theme="dark"] .input-wrapper:focus-within {
  border-color: rgba(129, 140, 248, 0.55);
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.42), 0 0 0 1px rgba(129, 140, 248, 0.18);
}

[data-theme="dark"] .input-wrapper.voice-mode-active {
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.42), 0 0 0 1px rgba(16, 185, 129, 0.18);
}

.mode-tabs {
  display: flex;
  align-items: center;
  gap: 8px;
  row-gap: 8px;
  flex-wrap: wrap;
  margin: -2px -2px 10px;
  padding: 2px;
}

.companion-editor {
  width: 100%;
  border: none;
  background: transparent;
  resize: none;
  outline: none;
  font-family: inherit;
  font-size: 15px;
  color: #1C1C1E;
  max-height: 120px;
  min-height: 40px;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-y: auto;
  word-break: break-word;
}

[data-theme="dark"] .companion-editor {
  color: #FFFFFF;
}

.companion-editor:empty::before {
  content: attr(data-placeholder);
  color: #A8AFBD;
  pointer-events: none;
}

.companion-inline-token {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin: 0 2px;
  padding: 0 3px 0 5px;
  border-radius: 5px;
  background: #e7edff;
  color: #3652d8;
  font-weight: 600;
  line-height: 1.55;
  vertical-align: baseline;
  user-select: none;
}

.companion-token-remove {
  width: 15px;
  height: 15px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #6475d6;
  font-size: 14px;
  line-height: 13px;
  cursor: pointer;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity .15s ease, background .15s ease;
}

.companion-inline-token:hover .companion-token-remove,
.companion-inline-token:focus-within .companion-token-remove {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

.companion-token-remove:hover { background: rgba(54, 82, 216, .14); }
[data-theme="dark"] .companion-inline-token { background: #29345f; color: #dbe3ff; }
[data-theme="dark"] .companion-token-remove { color: #cbd5ff; }

.voice-inline-wrap {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

/* Common Instructions (Quick Prompts) */
.common-instructions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 8px 4px;
  animation: ci-rise 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}

@keyframes ci-rise {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

.ci-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #8A8A8E;
  letter-spacing: 0.2px;
}

.ci-label svg {
  color: #F59E0B;
}

[data-theme="dark"] .ci-label {
  color: #98989d;
}

.ci-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.instruction-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, rgba(89, 98, 243, 0.08), rgba(74, 101, 230, 0.04));
  border: 1px solid rgba(89, 98, 243, 0.18);
  border-radius: 100px;
  padding: 6px 12px;
  font-size: 12.5px;
  font-weight: 500;
  color: #4A65E6;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  max-width: 100%;
  white-space: nowrap;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.instruction-chip .chip-text {
  overflow: hidden;
  text-overflow: ellipsis;
}

.instruction-chip .chip-arrow {
  opacity: 0.6;
  transition: transform 0.2s ease, opacity 0.2s ease;
  flex-shrink: 0;
}

.instruction-chip:hover {
  background: linear-gradient(135deg, #5962f3, #4A65E6);
  border-color: transparent;
  color: #FFFFFF;
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(89, 98, 243, 0.28);
}

.instruction-chip:hover .chip-arrow {
  opacity: 1;
  transform: translateX(2px);
}

.instruction-chip:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(89, 98, 243, 0.2);
}

[data-theme="dark"] .instruction-chip {
  background: linear-gradient(135deg, rgba(110, 136, 249, 0.14), rgba(110, 136, 249, 0.06));
  border-color: rgba(110, 136, 249, 0.28);
  color: #A9B5FF;
}

[data-theme="dark"] .instruction-chip:hover {
  background: linear-gradient(135deg, #6E88F9, #4A65E6);
  border-color: transparent;
  color: #FFFFFF;
  box-shadow: 0 6px 16px rgba(110, 136, 249, 0.35);
}

/* Toolbar - Compact */
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: 2px;
  padding: 0 68px 0 0;
  gap: 12px;
}

.left-tools {
  display: flex;
  gap: 10px;
  align-items: center;
  flex: 1;
  min-width: 0;
  flex-wrap: wrap;
}

.tool-divider {
  width: 1px;
  height: 16px;
  background-color: #E5E5EA;
  margin: 0 4px;
  flex-shrink: 0;
}

[data-theme="dark"] .tool-divider {
  background-color: #2C2C2E;
}

.icon-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 50%;
  color: #697386;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-btn:hover {
  background: #F3F5FA;
  color: #46546A;
}

.attachment-btn svg {
  fill: currentColor;
}

.attachment-menu-wrap {
  position: relative;
  z-index: 6100;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.attachment-menu {
  position: absolute;
  left: 0;
  bottom: calc(100% + 8px);
  display: block;
  pointer-events: auto;
  z-index: 6000;
  width: 248px;
  padding: 8px;
  transform: none;
  border: 1px solid rgba(226, 232, 240, 0.95);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 16px 38px rgba(15, 23, 42, 0.18), 0 2px 8px rgba(15, 23, 42, 0.06);
  animation: attachment-menu-in 0.14s ease-out;
}

.attachment-menu.is-below {
  top: calc(100% + 8px);
  bottom: auto;
}

@keyframes attachment-menu-in {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

.attachment-menu.is-below {
  animation-name: attachment-menu-in-below;
}

@keyframes attachment-menu-in-below {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}

.attachment-menu-heading {
  padding: 3px 8px 7px;
  color: #64748B;
  font-size: 11px;
  font-weight: 700;
}

.attachment-menu-option {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 10px;
  padding: 9px 8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #1E293B;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.16s ease;
}

.attachment-menu-option:hover,
.attachment-menu-option:focus-visible {
  outline: none;
  background: #F1F5F9;
}

.attachment-menu-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  border-radius: 8px;
}

.workspace-menu-icon {
  color: #047857;
  background: rgba(5, 150, 105, 0.11);
}

.context-menu-icon {
  color: #2563EB;
  background: rgba(37, 99, 235, 0.10);
}

.attachment-menu-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.attachment-menu-copy strong {
  font-size: 12px;
  font-weight: 700;
}

.attachment-menu-copy small {
  overflow: hidden;
  color: #64748B;
  font-size: 10px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

[data-theme="dark"] .attachment-menu {
  border-color: rgba(148, 163, 184, 0.22);
  background: rgba(25, 29, 38, 0.98);
  box-shadow: 0 18px 42px rgba(0, 0, 0, 0.42);
}

[data-theme="dark"] .attachment-menu-heading,
[data-theme="dark"] .attachment-menu-copy small {
  color: #94A3B8;
}

[data-theme="dark"] .attachment-menu-option {
  color: #E2E8F0;
}

[data-theme="dark"] .attachment-menu-option:hover,
[data-theme="dark"] .attachment-menu-option:focus-visible {
  background: rgba(148, 163, 184, 0.14);
}

[data-theme="dark"] .icon-btn {
  color: #B8C0CF;
}

[data-theme="dark"] .icon-btn:hover {
  background: rgba(74, 101, 230, 0.16);
  color: #E4E9FF;
}

[data-theme="dark"] .attachment-btn {
  color: #C4CBDA;
}

[data-theme="dark"] .attachment-btn:hover {
  background: rgba(129, 140, 248, 0.16);
  color: #FFFFFF;
}

.tool-tab {
  background: #FFFFFF;
  border: 1px solid rgba(226, 232, 240, 0.95);
  cursor: pointer;
  padding: 7px 11px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  gap: 5px;
  color: #46546A;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.04);
  transition: transform 0.2s, box-shadow 0.2s, background 0.2s, border-color 0.2s, color 0.2s;
}

[data-theme="dark"] .tool-tab {
  background: #222733;
  border-color: rgba(148, 163, 184, 0.16);
  color: #B8C0CF;
  box-shadow: none;
}

.tool-tab.active {
  background: linear-gradient(180deg, #4C68E8 0%, #3652D8 100%);
  border-color: transparent;
  color: #FFFFFF;
  box-shadow: 0 6px 16px rgba(54, 82, 216, 0.32), inset 0 1px 0 rgba(255, 255, 255, 0.18);
  transform: translateY(-1px);
}

.tool-tab.active:hover {
  filter: brightness(0.95);
}

@media (hover: hover) and (pointer: fine) {
  .tool-tab:hover {
    background: #F4F6FB;
    border-color: #C9D2E0;
    color: #3A4761;
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.06);
  }
}

.mode-tabs .tool-tab {
  position: relative;
  z-index: 1;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 700;
  min-height: 30px;
  gap: 5px;
  background: #FFFFFF;
  border-color: #E4E9F2;
  color: #64748B;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
}

.mode-tabs .tool-tab svg {
  width: 14px;
  height: 14px;
  stroke-width: 2.2;
  opacity: 0.82;
}

/* 统一的状态视觉：未激活 = 中性灰描边胶囊，激活 = 品牌色实心胶囊。
 * 悬停只用浅底色区分，不再与激活态共用同一套颜色。 */
.mode-tabs .tool-tab.active {
  background: linear-gradient(180deg, #4C68E8 0%, #3652D8 100%);
  border-color: transparent;
  color: #FFFFFF;
  box-shadow: 0 4px 12px rgba(54, 82, 216, 0.30), inset 0 1px 0 rgba(255, 255, 255, 0.16);
  transform: none;
  z-index: 2;
}

.mode-tabs .tool-tab.active svg {
  opacity: 1;
}

@media (hover: hover) and (pointer: fine) {
  .mode-tabs .tool-tab:hover {
    background: #F4F6FB;
    border-color: #C9D2E0;
    color: #46536B;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
    transform: none;
  }

  .mode-tabs .tool-tab.active:hover {
    background: linear-gradient(180deg, #4662E4 0%, #3049CC 100%);
    color: #FFFFFF;
  }
}

.workspace-mode-tab {
  gap: 5px;
}

.workspace-count {
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: #8792A6;
  color: #FFFFFF;
  font-size: 10px;
  line-height: 16px;
  text-align: center;
}

.tool-tab.active .workspace-count {
  background: rgba(255, 255, 255, 0.30);
}

.workspace-file-strip {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  margin: -2px 0 9px;
  overflow-x: auto;
  padding: 1px 1px 3px;
  scrollbar-width: thin;
  -ms-overflow-style: none;
}

.workspace-file-strip::-webkit-scrollbar {
  height: 3px;
}

.active-companion-strip {
  display: flex;
  gap: 6px;
  width: 100%;
  overflow-x: auto;
  padding: 1px 1px 7px;
  scrollbar-width: thin;
}

.active-companion-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex: 0 0 auto;
  max-width: 190px;
  height: 28px;
  padding: 0 7px;
  border: 1px solid rgba(112, 130, 246, .28);
  border-radius: 999px;
  background: linear-gradient(180deg, #f7f8ff, #eef1ff);
  color: #4054b8;
  font-size: 11px;
  font-weight: 700;
}

.companion-chip-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.companion-chip-remove { border: 0; background: transparent; color: #6977ba; cursor: pointer; padding: 0 2px; font-size: 15px; line-height: 1; }

.companion-mention-popover {
  position: absolute;
  z-index: 2;
  left: 12px;
  right: 12px;
  bottom: 100%;
  width: auto;
  max-height: 176px;
  overflow: auto;
  margin: 0 0 8px;
  padding: 3px;
  border: 1px solid rgba(148, 163, 184, .22);
  border-radius: 9px;
  background: rgba(248, 250, 252, .96);
}
.companion-mention-option { display: flex; align-items: center; width: 100%; border: 0; border-radius: 7px; background: transparent; padding: 7px 10px; text-align: left; cursor: pointer; }
.companion-mention-option.active, .companion-mention-option:hover { background: #eef2ff; }
.mention-option-name { display: block; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #1e293b; font-size: 13px; line-height: 20px; }
.mention-empty { padding: 8px; color: #94a3b8; font-size: 11px; }
.active-companion-summary { max-width: 220px; }
.companion-more-count { color: #6875c9; font-size: 10px; }

[data-theme="dark"] .active-companion-chip { background: #242b4c; border-color: #3f4b86; color: #d6dcff; }
[data-theme="dark"] .companion-mention-popover { background: rgba(35, 38, 46, .98); border-color: rgba(255,255,255,.12); }
[data-theme="dark"] .companion-mention-option.active, [data-theme="dark"] .companion-mention-option:hover { background: #30375b; }
[data-theme="dark"] .mention-option-name { color: #f8fafc; }

.workspace-file-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  min-width: 0;
  max-width: 190px;
  height: 28px;
  padding: 0 9px 0 6px;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  background: #F8FAFC;
  color: #475569;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  transition: border-color .2s ease, background .2s ease, color .2s ease;
}

.workspace-file-chip:hover {
  border-color: #A5B4FC;
  background: #EEF2FF;
  color: #3652D8;
}

.workspace-file-chip-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  min-width: 25px;
  height: 17px;
  padding: 0 3px;
  border-radius: 4px;
  background: #E0E7FF;
  color: #4F46E5;
  font-size: 9px;
  font-weight: 800;
}

.workspace-file-chip-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

[data-theme="dark"] .workspace-file-chip {
  border-color: rgba(148, 163, 184, .2);
  background: #202633;
  color: #CBD5E1;
}

[data-theme="dark"] .workspace-file-chip:hover {
  border-color: rgba(129, 140, 248, .62);
  background: rgba(74, 101, 230, .2);
  color: #E4E9FF;
}

[data-theme="dark"] .workspace-file-chip-mark {
  background: rgba(129, 140, 248, .22);
  color: #C7D2FE;
}

.toolbar .tool-tab,
.toolbar .active-role-chip {
  padding: 6px 10px;
  font-size: 12px;
  min-height: 30px;
}

[data-theme="dark"] .tool-tab.active {
  background: linear-gradient(180deg, #5B74F0 0%, #4359DB 100%);
  border-color: rgba(129, 140, 248, 0.45);
  color: #FFFFFF;
  box-shadow: 0 6px 18px rgba(74, 101, 230, 0.38), inset 0 1px 0 rgba(255, 255, 255, 0.20);
}

@media (hover: hover) and (pointer: fine) {
  [data-theme="dark"] .tool-tab:hover {
    background: rgba(148, 163, 184, 0.12);
    border-color: rgba(148, 163, 184, 0.28);
    color: #E5E9F2;
  }
}

[data-theme="dark"] .mode-tabs .tool-tab {
  background: #202633;
  border-color: rgba(148, 163, 184, 0.16);
  color: #97A1B4;
}

[data-theme="dark"] .mode-tabs .tool-tab.active {
  background: linear-gradient(180deg, #5B74F0 0%, #4359DB 100%);
  border-color: rgba(129, 140, 248, 0.45);
  color: #FFFFFF;
  box-shadow: 0 4px 14px rgba(74, 101, 230, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.18);
}

@media (hover: hover) and (pointer: fine) {
  [data-theme="dark"] .mode-tabs .tool-tab:hover {
    background: rgba(148, 163, 184, 0.10);
    border-color: rgba(148, 163, 184, 0.26);
    color: #D4DAE6;
    box-shadow: none;
  }

  [data-theme="dark"] .mode-tabs .tool-tab.active:hover {
    background: linear-gradient(180deg, #5269EC 0%, #3B50D0 100%);
    color: #FFFFFF;
  }
}

.image-config-selectors {
  display: flex;
  gap: 4px;
  align-items: center;
}

.image-config-select {
  background: #FFFFFF;
  border: 1px solid #E8ECF4;
  border-radius: 999px;
  padding: 5px 7px;
  font-size: 11px;
  color: #46546A;
  outline: none;
  cursor: pointer;
  transition: all 0.2s;
  max-width: 96px;
}

.image-config-select:hover {
  border-color: #5962f3;
}

[data-theme="dark"] .image-config-select {
  background: #202633;
  border-color: rgba(148, 163, 184, 0.16);
  color: #D7DEEA;
}

[data-theme="dark"] .image-config-select:hover {
  border-color: #6E88F9;
}

.send-btn {
  position: absolute;
  right: 24px;
  bottom: 22px;
  background: #4057D8;
  color: #FFFFFF;
  border: none;
    border-radius: 22px;
  width: 46px;
  height: 46px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
  z-index: 5;
}

[data-theme="dark"] .send-btn {
  background: linear-gradient(180deg, #6577FF 0%, #4659E8 100%);
  color: #FFFFFF;
}

.send-btn:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #1D44FF;
}

/* Voice Input */
.voice-btn {
  color: #7A8596;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.9), rgba(244, 247, 255, 0.9));
  border: 1px solid rgba(226, 232, 240, 0.85);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
  transition: all 0.2s ease;
}

.voice-btn.active {
  color: #059669;
  border-color: rgba(16, 185, 129, 0.28);
  background: linear-gradient(180deg, rgba(240, 253, 244, 0.98), rgba(220, 252, 231, 0.94));
  box-shadow: 0 8px 22px rgba(16, 185, 129, 0.16);
}

.voice-btn {
  /* Hold-to-talk: keep the browser from scrolling, selecting or opening a menu while the button is held. */
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}

.voice-btn:disabled {
  cursor: wait;
}

[data-theme="dark"] .voice-btn {
  background: linear-gradient(180deg, #202633 0%, #1C2230 100%);
  border: 1px solid rgba(148, 163, 184, 0.18);
  color: #C7D0DE;
}

[data-theme="dark"] .voice-btn.active {
  color: #A7F3D0;
  border-color: rgba(52, 211, 153, 0.24);
  background: linear-gradient(180deg, rgba(16, 185, 129, 0.16), rgba(17, 94, 89, 0.2));
}

@keyframes pulse-ring {
  0% { transform: scale(0.9); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.8; }
  100% { transform: scale(0.9); opacity: 1; }
}

.pulse-animation {
  animation: pulse-ring 1.5s infinite ease-in-out;
}

/* Scroll Tip */
.scroll-tip {
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  padding-bottom: 10px;
  z-index: 50;
  pointer-events: none;
}

.scroll-tip-content {
  pointer-events: auto;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  padding: 8px 16px;
  border-radius: 24px;
  font-size: 0.85rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

[data-theme="dark"] .scroll-tip-content {
  background: rgba(255, 255, 255, 0.6);
  color: #000;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.scroll-tip-content:hover {
  transform: translateY(-2px);
}

/* Attachment Preview */
.attachment-preview {
  padding: 8px 8px 4px;
}

.uploading-spinner {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #8A8A8E;
}

.spinner {
  width: 16px;
  height: 16px;
  animation: rotate 2s linear infinite;
}

.spinner .path {
  stroke: #4A65E6;
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
}

.attachments-list {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  padding: 6px;
}

.preview-item {
  position: relative;
}

.preview-img-container {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #E5E5EA;
  position: relative;
  cursor: pointer;
}

.preview-img-container img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-file-container {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #F3F4F6;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #E5E5EA;
  max-width: 200px;
  transition: all 0.2s ease;
}

.preview-file-container.clickable {
  cursor: pointer;
}

.preview-file-container.clickable:hover {
  background: #E5E7EB;
  border-color: #D1D5DB;
}

[data-theme="dark"] .preview-file-container {
  background: #1C1C1E;
  border-color: #2C2C2E;
  color: #A1A1A5;
}

[data-theme="dark"] .preview-file-container.clickable:hover {
  background: #2C2C2E;
  border-color: #3C3C3E;
}

.file-name {
  font-size: 12px;
  color: #4B5563;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.remove-btn {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #FF3B30;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: transform 0.2s;
}

[data-theme="dark"] .remove-btn {
  background: #FF3B30;
  color: white;
}

.remove-btn:hover {
  transform: scale(1.05);
}

/* Role Chip & Panel */
.active-role-chip {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 10px;
  background-color: #FFFFFF;
  border: 1px solid rgba(226, 232, 240, 0.95);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  color: #46546A;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  box-shadow: 0 3px 10px rgba(15, 23, 42, 0.035);
}

[data-theme="dark"] .active-role-chip {
  background-color: #2C2C2E;
  border-color: rgba(255,255,255,0.05);
  color: #FFFFFF;
}

.remove-role-btn {
  background: none;
  border: none;
  color: #8A8A8E;
  font-size: 16px;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 2px;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  transition: all 0.2s;
}

.remove-role-btn:hover {
  background-color: rgba(0,0,0,0.1);
  color: #1C1C1E;
}

[data-theme="dark"] .remove-role-btn:hover {
  background-color: rgba(255,255,255,0.2);
  color: #FFFFFF;
}

/* Drag Overlay */
.drag-overlay {
  position: fixed;
  inset: 0;
  background: rgba(248, 250, 252, 0.96);
  border: 3px dashed var(--text-primary);
  border-radius: 0;
  z-index: 5000;
  display: flex;
  align-items: center;
  justify-content: center;
}

[data-theme="dark"] .drag-overlay {
  background: rgba(17, 17, 17, 0.9);
}

.drag-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  width: min(680px, calc(100% - 28px));
  color: var(--text-primary);
}

.drag-heading {
  font-size: 17px;
  font-weight: 650;
}

.drag-subheading,
.drag-footnote {
  color: var(--text-secondary, #6b7280);
  font-size: 11px;
}

.drag-targets {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
  margin-top: 14px;
}

.drag-target {
  min-height: 160px;
  padding: 18px 12px;
  border: 1px dashed rgba(107, 114, 128, .55);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-align: center;
  cursor: copy;
  position: relative;
  transition: border-color .18s ease, background-color .18s ease, transform .2s cubic-bezier(.2, .8, .3, 1.2), box-shadow .2s ease, opacity .18s ease;
}

/* Children must not swallow drag events, otherwise the card flickers */
.drag-target > * {
  pointer-events: none;
}

.drag-target.is-active {
  transform: scale(1.08);
  border-style: solid;
  border-width: 2px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, .16);
  z-index: 1;
}

/* When one card is targeted, de-emphasise the other */
.drag-targets:has(.is-active) .drag-target:not(.is-active) {
  opacity: .55;
  transform: scale(.96);
}

.drag-target-badge {
  position: absolute;
  top: 8px;
  right: 10px;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 10px;
  line-height: 16px;
  color: #fff;
  background: #2563eb;
  opacity: 0;
  transition: opacity .18s ease;
}

.drag-target.is-default .drag-target-badge {
  opacity: 1;
}

.drag-target strong {
  font-size: 14px;
}

.drag-target small {
  color: var(--text-secondary, #6b7280);
  font-size: 11px;
  line-height: 1.45;
}

.drag-target-attachment {
  color: #2563eb;
  background: rgba(37, 99, 235, .055);
}

.drag-target-attachment.is-default {
  border-color: rgba(37, 99, 235, .6);
}

.drag-target-attachment.is-active {
  border-color: #2563eb;
  background: rgba(37, 99, 235, .13);
}

.drag-target-workspace {
  color: #047857;
  background: rgba(5, 150, 105, .055);
}

.drag-target-workspace.is-active {
  border-color: #059669;
  background: rgba(5, 150, 105, .13);
}

.drag-footnote {
  margin-top: 9px;
}

@media (max-width: 768px) {
  .input-area {
    width: calc(100% - 18px);
    bottom: 0.75rem;
  }

  .input-wrapper {
    border-radius: 24px;
    padding: 12px 12px 14px;
  }

  .companion-editor {
    font-size: 14px;
  }

  .drag-overlay {
    border-radius: 12px;
  }

  .drag-targets {
    gap: 8px;
  }

  .drag-target {
    min-height: 138px;
    padding: 12px 7px;
  }

  .drag-target strong {
    font-size: 13px;
  }

  .drag-target small {
    font-size: 10px;
  }

  .mode-tabs {
    flex-wrap: nowrap;
    gap: 7px;
    overflow-x: auto;
    margin: -2px -2px 0px;
    padding: 2px 2px 4px;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .mode-tabs::-webkit-scrollbar {
    display: none;
  }
  
  .tool-tab span {
    display: inline;
  }
  
  .tool-tab, .active-role-chip {
    padding: 6px 10px;
    flex-shrink: 0;
    font-size: 12px;
  }

  .mode-tabs .tool-tab {
    min-height: 30px;
    padding: 6px 10px;
    font-size: 12px;
  }

  .mode-tabs .tool-tab svg {
    width: 14px;
    height: 14px;
  }

  .mode-tabs .image-config-selectors {
    flex-shrink: 0;
  }

  .mode-tabs .image-config-select {
    padding: 5px 7px;
    max-width: 86px;
  }

  .left-tools {
    gap: 8px;
    flex-wrap: nowrap;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding: 0 2px 2px 0;
  }

  .left-tools.has-attachment-menu {
    overflow: visible;
  }
  
  .left-tools::-webkit-scrollbar {
    display: none;
  }

  .toolbar {
    gap: 10px;
    align-items: flex-end;
    padding-right: 62px;
  }

  .send-btn {
    right: 16px;
    bottom: 16px;
  }
}

@media (max-width: 430px) {
  .toolbar .tool-tab span,
  .role-name {
    max-width: 4.5em;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .mode-tabs .tool-tab span {
    max-width: none;
  }

  .tool-tab,
  .active-role-chip {
    padding: 6px 9px;
  }

  .tool-divider {
    display: none;
  }
}

/* Visual reference layout: spacious desktop composer. The interaction
 * structure above stays unchanged; these rules only establish the measured
 * proportions, spacing and palette shown in the reference. */
@media (min-width: 769px) {
  .input-area {
    width: calc(100% - 56px);
    max-width: none;
    bottom: 31px;
  }

  .input-wrapper {
    box-sizing: border-box;
    height: 416px;
    min-height: 416px;
    padding: 29px 38px 38px;
    border: 3px solid #e5edff;
    border-radius: 49px;
    background: #ffffff;
    box-shadow: 0 3px 16px rgba(89, 126, 205, 0.04);
  }

  .input-wrapper:focus-within {
    border-color: #d6e3ff;
    box-shadow: 0 4px 18px rgba(89, 126, 205, 0.08);
  }

  .mode-tabs {
    flex-wrap: nowrap;
    gap: 18px;
    margin: 0;
    margin-left: -10px;
    padding: 0;
    min-height: 92px;
    overflow: visible;
  }

  .mode-tabs .tool-tab {
    box-sizing: border-box;
    flex: 0 0 auto;
    justify-content: center;
    min-height: 92px;
    padding: 0 24px;
    border: 2px solid #e2e8f2;
    border-radius: 48px;
    background: #ffffff;
    box-shadow: none;
    color: #64748b;
    font-size: 27px;
    font-weight: 500;
    letter-spacing: 0;
    line-height: 1;
    gap: 17px;
  }

  .mode-tabs .tool-tab svg {
    flex: 0 0 auto;
    width: 43px;
    height: 43px;
    stroke-width: 1.8;
  }

  .mode-thinking { width: 286px; }
  .mode-web-search { width: 280px; }
  .mode-plan { width: 250px; }
  .mode-workspace { width: 235px; }
  .mode-image-gen { width: 235px; }

  .mode-tabs .tool-tab.active {
    background: linear-gradient(180deg, #4c68e8 0%, #3652d8 100%);
    border-color: transparent;
    color: #ffffff;
    box-shadow: 0 8px 20px rgba(54, 82, 216, 0.30);
  }

  .mode-tabs .tool-tab:hover {
    background: #f4f6fb;
    border-color: #c9d2e0;
    color: #46536b;
    box-shadow: none;
  }

  .mode-tabs .tool-tab.active:hover {
    background: linear-gradient(180deg, #4662e4 0%, #3049cc 100%);
    color: #ffffff;
  }

  .companion-editor {
    box-sizing: border-box;
    min-height: 52px;
    height: 52px;
    margin-top: 52px;
    color: #2b416d;
    font-size: 32px;
    font-weight: 400;
    line-height: 1.35;
  }

  .companion-editor:empty::before {
    color: #8094b8;
  }

  .toolbar {
    align-items: center;
    margin-top: 37px;
    padding: 0;
    gap: 24px;
  }

  .left-tools {
    flex-wrap: nowrap;
    gap: 24px;
    align-items: center;
  }

  .toolbar .tool-tab,
  .toolbar .active-role-chip {
    box-sizing: border-box;
    min-height: 95px;
    border: 0;
    border-radius: 48px;
    background: #f4f8ff;
    box-shadow: none;
    color: #2f4879;
    font-size: 27px;
    font-weight: 500;
    gap: 17px;
  }

  .toolbar .role-selector,
  .toolbar .active-role-chip {
    width: 314px;
    justify-content: center;
    padding: 0 24px;
  }

  .toolbar .role-selector svg {
    width: 43px;
    height: 43px;
  }

  .toolbar .icon-btn {
    width: 98px;
    height: 95px;
    border-radius: 48px;
    background: #f4f8ff;
    color: #2f4879;
    box-shadow: none;
  }

  .toolbar .icon-btn svg {
    width: 43px;
    height: 43px;
  }

  .toolbar .icon-btn:hover,
  .toolbar .role-selector:hover {
    background: #edf4ff;
    color: #2359d7;
  }

  .send-btn {
    right: 38px;
    bottom: 38px;
    width: 132px;
    height: 95px;

    background: #2865ed;
  }

  .send-btn svg {
    width: 39px;
    height: 39px;
  }

  .send-btn:disabled {
    background: #2865ed;
    opacity: 1;
  }
}

@media (min-width: 769px) and (max-width: 1200px) {
  .input-area {
    width: calc(100% - 32px);
    bottom: 20px;
  }

  .input-wrapper {
    height: 330px;
    min-height: 330px;
    padding: 20px 24px 26px;
    border-radius: 34px;
  }

  .mode-tabs {
    gap: 8px;
    min-height: 58px;
    overflow-x: auto;
  }

  .mode-tabs .tool-tab {
    min-height: 58px;
    width: auto;
    padding: 0 17px;
    font-size: 16px;
    gap: 8px;
  }

  .mode-tabs .tool-tab svg {
    width: 25px;
    height: 25px;
  }

  .companion-editor {
    margin-top: 34px;
    height: 42px;
    min-height: 42px;
    font-size: 22px;
  }

  .toolbar {
    margin-top: 26px;
    gap: 12px;
  }

  .left-tools { gap: 12px; }

  .toolbar .tool-tab,
  .toolbar .active-role-chip {
    min-height: 64px;
    width: auto;
    padding: 0 18px;
    font-size: 18px;
    gap: 9px;
  }

  .toolbar .icon-btn {
    width: 64px;
    height: 64px;
  }

  .toolbar .role-selector svg,
  .toolbar .icon-btn svg {
    width: 28px;
    height: 28px;
  }

  .send-btn {
    right: 24px;
    bottom: 26px;
    width: 88px;
    height: 64px;
  
  }

  .send-btn svg { width: 29px; height: 29px; }
}

/* Keep the component's existing responsive dimensions. The visual treatment
 * above must not make the composer grow with a wide application shell. */
@media (min-width: 769px) {
  .input-area {
    width: 90%;
    max-width: 768px;
    bottom: 1.5rem;
  }

  .input-wrapper {
    height: auto;
    min-height: 0;
    padding: 14px 18px 18px;
    border-radius: 28px;
    background: #ffffff;
    box-shadow: none;
  }

  .input-wrapper:focus-within,
  .input-wrapper.voice-mode-active,
  [data-theme="dark"] .input-wrapper,
  [data-theme="dark"] .input-wrapper:focus-within,
  [data-theme="dark"] .input-wrapper.voice-mode-active {
    box-shadow: none;
  }

  .mode-tabs {
    flex-wrap: wrap;
    gap: 8px;
    margin: -2px -2px 10px;
    padding: 2px;
    min-height: 0;
    overflow: visible;
  }

  .mode-tabs .tool-tab {
    width: auto;
    min-height: 30px;
    padding: 6px 10px;
    border: 1px solid #e8ecf4;
    border-radius: 999px;
    background: #fff;
    box-shadow: 0 3px 10px rgba(15, 23, 42, 0.035);
    font-size: 12px;
    font-weight: 700;
    gap: 5px;
  }

  .mode-tabs .tool-tab svg {
    width: 14px;
    height: 14px;
  }

  .mode-thinking,
  .mode-web-search,
  .mode-plan,
  .mode-workspace,
  .mode-image-gen {
    width: auto;
  }

  .companion-editor {
    height: auto;
    min-height: 40px;
    margin-top: 0;
    font-size: 15px;
    line-height: 1.5;
  }

  .toolbar {
    align-items: flex-end;
    margin-top: 2px;
    padding: 0 68px 0 0;
    gap: 12px;
  }

  .left-tools {
    flex-wrap: wrap;
    gap: 10px;
  }

  .toolbar .tool-tab,
  .toolbar .active-role-chip {
    width: auto;
    min-height: 30px;
    padding: 6px 10px;
    border: 1px solid rgba(226, 232, 240, 0.95);
    border-radius: 999px;
    background: #fff;
    box-shadow: 0 3px 10px rgba(15, 23, 42, 0.035);
    font-size: 12px;
    font-weight: 700;
    gap: 5px;
  }

  .toolbar .role-selector svg,
  .toolbar .icon-btn svg {
    width: 20px;
    height: 20px;
  }

  .toolbar .icon-btn {
    width: 36px;
    height: 36px;
    border-radius: 50%;
  }

  .send-btn {
    right: 24px;
    bottom: 22px;
    width: 64px;
    height: 46px;

  }

  .send-btn svg {
    width: 18px;
    height: 18px;
  }
}
</style>

<style>
/* Search-style mention highlighting is injected with v-html. */
.mention-match {
  padding: 0;
  background: transparent;
  color: #3652d8;
  font-weight: 700;
}

[data-theme="dark"] .mention-match {
  color: #9aa8ff;
}

/* These nodes are created with innerHTML inside the contenteditable editor,
   so they do not receive Vue's scoped-style attribute. Keep their visual and
   interaction styles global. */
.companion-inline-token {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin: 0 2px;
  padding: 0 3px 0 5px;
  border-radius: 5px;
  background: #e7edff;
  color: #3652d8;
  font-weight: 600;
  line-height: 1.55;
  vertical-align: baseline;
  user-select: none;
}

.companion-token-remove {
  display: none;
  width: 15px;
  height: 15px;
  padding: 0;
  border: 0;
  outline: 0;
  appearance: none;
  -webkit-appearance: none;
  border-radius: 50%;
  background: transparent;
  color: #6475d6;
  font: inherit;
  font-size: 14px;
  line-height: 13px;
  cursor: pointer;
}

.companion-inline-token:hover .companion-token-remove,
.companion-inline-token:focus-within .companion-token-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.companion-token-remove:hover {
  background: rgba(54, 82, 216, .14);
}

[data-theme="dark"] .companion-inline-token {
  background: #29345f;
  color: #dbe3ff;
}

[data-theme="dark"] .companion-token-remove {
  color: #cbd5ff;
}
</style>
